import { plural } from './format';

/**
 * **The source of truth.** Every other catalog is checked against this one by
 * the compiler (`es.ts` declares `: Messages`), so this file decides what keys
 * exist and what parameters each of them takes.
 *
 * Every string here is a byte-for-byte transcription of what the staff PWA
 * printed before this file existed. That is not a stylistic preference: the
 * suites in `ui/`, `outbox/` and `mirror/` assert this app's exact English, and
 * they were not edited for this wave. A changed word here is a failing test,
 * which is the cheapest possible proof that translating a surface did not
 * quietly rewrite it.
 *
 * ## What belongs here, and what does not
 *
 * Here: **words this bundle writes** — buttons, labels, headings, empty states,
 * the sentences a screen uses to explain itself, and the `describe.what`
 * snapshots the Tasks list shows. That is docs/i18n-design.md §2's Domain A.
 *
 * Not here, and the boundary is the one somebody adding a key will test:
 *
 *  - **Tenant content** — service, product and client names, notes, `item_name`,
 *    `vessel`, `room_type` — is *data* (Domain B). It arrives as the tenant
 *    typed it and is printed as the tenant typed it. Translating it would be the
 *    platform putting words in a business's mouth.
 *  - **Server sentences** are Domain C, written by the service that knows what
 *    happened: `ApiFailure.message` (an Orders refusal, a Departures or front
 *    desk failure, the pay-links `503`), `order.status` and `stay.status`,
 *    `session.modules`, the weekday `name` on the visits-by-weekday report,
 *    `entry.summary` from `reporting-api`, and the certificate `state`. Each of
 *    those is printed verbatim, and the component that prints it says so. A key
 *    here that paraphrased one would be a second wording of a fact we do not
 *    own.
 *  - The **waterfall refusals** (`money/waterfall.ts`) are a transcription of
 *    the server's own function, pinned to it by a test that copies the server's
 *    expected numbers *and words*. They are Domain C wearing a local
 *    implementation, and translating the copy would make the till and the
 *    receipt disagree.
 */

export const en = {
  /* ---------------------------------------------------------------- */
  /* Shared                                                            */
  /* ---------------------------------------------------------------- */

  'common.add': 'Add',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.name': 'Name',
  'common.notes': 'Notes',
  'common.open': 'Open',
  'common.price': 'Price',
  'common.remove': 'Remove',
  'common.restore': 'Restore',
  'common.retire': 'Retire',
  'common.save': 'Save',

  /* ---------------------------------------------------------------- */
  /* The shell (App.tsx)                                               */
  /* ---------------------------------------------------------------- */

  /**
   * The tab labels. `TabSpec.id` is the identity and never moves; the label is
   * looked up here **at render**, so the navigation stays the data table
   * `visibleTabs` filters and a language is not baked into a module constant.
   */
  'tabs.today': 'Today',
  'tabs.calendar': 'Calendar',
  'tabs.orders': 'Orders',
  'tabs.reservations': 'Reservations',
  'tabs.parcels': 'Parcels',
  'tabs.departures': 'Departures',
  'tabs.frontdesk': 'Front desk',
  'tabs.albums': 'Albums',
  'tabs.clients': 'Clients',
  'tabs.giftcards': 'Gift cards',
  'tabs.tasks': 'Tasks',
  'tabs.reports': 'Reports',
  'tabs.settings': 'Settings',
  'tabs.newsletter': 'Newsletter',
  'tabs.team': 'Team',
  'tabs.navLabel': 'Sections',

  /* ---------------------------------------------------------------- */
  /* The status strip                                                  */
  /* ---------------------------------------------------------------- */

  'status.online': 'Online',
  'status.reconnecting': 'Reconnecting',
  'status.offline': 'Offline',
  'status.notLiveTitle': 'Another system may still be taking bookings.',
  'status.waitingToSend': ({ count }: { count: number }) => `${String(count)} waiting to send`,
  'status.needsYou': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'needs you', 'need you')}`,
  'status.signOut': 'Sign out',
  /* The console's read-only look ("see what she sees"; `auth/support.ts`). */
  'status.supportBanner': ({ operator, until }: { operator: string; until: string }) =>
    `Support view — read-only — operator ${operator} until ${until}`,
  'status.supportEnd': 'End support view',
  'status.supportEnded': 'Support view ended',
  'status.supportEndedHint':
    'Fifteen minutes is the whole of it. Open another look from the console; each one is its own audit row.',
  'actions.supportRefused':
    'This is a support view: it may look at the book but not change it. Nothing was queued.',
  /* A real `readonly` seat that pressed a door the screen still drew (L15). */
  'actions.readOnlyRefused': 'This seat can read the book but not change it. Nothing was queued.',

  /* ---------------------------------------------------------------- */
  /* Sign in                                                           */
  /* ---------------------------------------------------------------- */

  /**
   * The dev banner is spliced around two `<code>` runs, which is the one thing
   * this catalog otherwise refuses to do. It is written as lead/middle because
   * the shape — *sentence, an executable's name, a connective, a flag* — is the
   * same in both languages, and because a `<code>` is what tells an engineer
   * that `AUTH_FAKE_TOKENS=1` is something to type rather than something to
   * read. The trailing full stop is punctuation in the JSX, not a catalog entry.
   */
  'signIn.devLead':
    'Development sign-in. Any email works and the token is unsigned — this build talks only to a ',
  'signIn.devMid': ' started with ',
  'signIn.errorUnavailable': 'Could not reach the sign-in service. Check the connection.',
  'signIn.errorMismatch': 'That email and password did not match.',
  'signIn.email': 'Email',
  'signIn.password': 'Password',
  'signIn.signingIn': 'Signing in…',
  'signIn.submit': 'Sign in',

  /* ---------------------------------------------------------------- */
  /* The personality (docs/lovespa-personality.md)                     */
  /* ---------------------------------------------------------------- */

  /**
   * **Copy follows the icon.** These two are the whole of the rule the
   * personality doc states by example — a tenant on the heart is pointed at
   * "the heart button" and a tenant on the plus at "the + button" — and they
   * live in the catalog rather than in `brand.tsx` so the glyph and the word for
   * it cannot end up in different languages.
   */
  'brand.heartButton': 'the heart button',
  'brand.plusButton': 'the + button',

  /* ---------------------------------------------------------------- */
  /* Where a booking came from                                         */
  /* ---------------------------------------------------------------- */

  'source.partner': 'concierge',
  'source.walkIn': 'walk-in',
  'source.guestWeb': 'website',
  'source.import': 'imported',
  'source.counter': 'counter',

  /* ---------------------------------------------------------------- */
  /* Staleness and freshness (mirror/time.ts, spec §8.1)               */
  /* ---------------------------------------------------------------- */

  'time.neverSynced': 'never synced',
  'time.syncedJustNow': 'synced just now',
  'time.lastSyncedMinutes': ({ clock, minutes }: { clock: string; minutes: number }) =>
    `last synced ${clock} · ${String(minutes)} min ago`,
  'time.lastSyncedHours': ({ clock, hours }: { clock: string; hours: number }) =>
    `last synced ${clock} · ${String(hours)} h ago`,
  'time.notReadYet': 'not read yet',
  'time.readJustNow': 'read just now',
  'time.readMinutes': ({ clock, minutes }: { clock: string; minutes: number }) =>
    `read ${clock} · ${String(minutes)} min ago`,
  'time.readHours': ({ clock, hours }: { clock: string; hours: number }) =>
    `read ${clock} · ${String(hours)} h ago`,

  /**
   * "2 PM" / "2:30 PM" — the spec's own phrasing for a task's collision time.
   *
   * The meridiem's **casing is the language's business**, which is why it is
   * applied inside the catalog entry rather than by the formatter: English
   * writes `PM`, and CLDR renders Spanish as `p. m.` — lower case, with full
   * stops — which is right as it comes. A `.toUpperCase()` in a shared function
   * would have shouted `P. M.` at a Spanish reader.
   */
  'time.clockOnHour': ({ hour, meridiem }: { hour: string; meridiem: string }) =>
    `${hour} ${meridiem.toUpperCase()}`,
  'time.clockWithMinutes': ({
    hour,
    minute,
    meridiem,
  }: {
    hour: string;
    minute: string;
    meridiem: string;
  }) => `${hour}:${minute} ${meridiem.toUpperCase()}`,

  /* ---------------------------------------------------------------- */
  /* Today                                                             */
  /* ---------------------------------------------------------------- */

  'today.quickSale': 'Quick sale',
  /**
   * The same button, when the Reservations screen has handed a pickup order to
   * the till (`ui/till.ts`). It is the *same* tap and the *same* sheet — the
   * word changes so the cashier who just pressed "Ring up" on another screen can
   * see where it went, rather than being told to find a button that looks like
   * every other day's.
   */
  'today.ringUpPickup': 'Ring up the pickup',
  'today.walkIn': 'Walk-in',
  'today.empty': 'Nothing on the book for today.',
  /**
   * The heart clause, and the leading space is load-bearing: it is appended to
   * the sentence above, and the **plus branch appends nothing at all**. The
   * named set adds; it never re-words the app for a tenant who chose nothing,
   * and `personality.test.tsx` asserts exactly that as bytes.
   */
  'today.emptyHeart': ({ button }: { button: string }) => ` Start one with ${button}.`,
  'today.duration': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  'today.waitingToSend': 'waiting to send',
  'today.checkoutWaiting': 'checkout waiting to send',
  'today.closeOut': 'Close out',
  'today.paymentLink': 'Payment link',
  'today.done': 'Done',
  'today.noShow': 'No-show',
  'today.cancel': 'Cancel',

  /**
   * Birthdays (B2). One line, assembled from the two clauses below so that
   * "today" alone, "tomorrow" alone and both together are one sentence rather
   * than three layouts — and so a language that orders it differently can.
   */
  'today.birthdays': ({ list }: { list: string }) => `Birthdays: ${list}`,
  'today.birthdaysToday': ({ names }: { names: string }) => `today — ${names}`,
  'today.birthdaysTomorrow': ({ names }: { names: string }) => `tomorrow — ${names}`,

  /**
   * Today's takings — one line, assembled from the clauses below so each
   * tender can put its word and its number in whichever order the language
   * reads them. The waiting clause is a **count** and never money: a queued
   * sale has no total on this device (`outbox/intents.ts`).
   */
  'today.takings': ({ total }: { total: string }) => `At the counter today: ${total}`,
  'today.takingsTender': ({ tender, amount }: { tender: string; amount: string }) =>
    `${tender} ${amount}`,
  'today.takingsNone': 'Nothing taken yet today',
  'today.takingsWaiting': ({ count }: { count: number }) =>
    `+ ${String(count)} ${plural(count, 'sale', 'sales')} waiting to send`,
  'today.takingsAsOf': ({ clock }: { clock: string }) => `as of ${clock}`,

  /* ---------------------------------------------------------------- */
  /* Requests from the guest site (docs/lovespa-discovery.md §2.3)     */
  /*                                                                   */
  /* A tenant whose site takes requests rather than bookings. The one  */
  /* verb on a message is "answered", and since X1 it is the server's   */
  /* fact (an `inquiry.answered` event) rather than a tick on one iPad. */
  /* ---------------------------------------------------------------- */

  /** Under the time, on every line. Nobody proved this name or this number. */
  'requests.unverified': 'not verified',
  'requests.noName': 'Someone',
  'requests.book': 'Book this',
  'requests.appointmentsTitle': 'Appointment requests',
  'requests.verified': 'verified number',
  'requests.new': 'new guest',
  'requests.holdsUntil': ({ when }: { when: string }) => `Held until ${when}`,
  'requests.confirm': 'Confirm',
  'requests.decline': 'Decline',
  'requests.propose': 'Another time',
  'requests.andTrust': 'and let her book herself next time',
  'requests.declineMessage': 'A note for her (optional)',
  'requests.proposeStart': 'Offer this time instead',
  'requests.send': 'Send',
  'requests.cancel': 'Back',
  'requests.proposed': 'Waiting for her answer',
  'requests.noticeQueued': 'Texted',
  'requests.noticeNoConsent': 'Not texted — she has not agreed to texts',
  'requests.noticeNoPhone': 'Not texted — no number on file',
  'requests.inquiriesTitle': 'Messages',
  'requests.answered': 'Answered',
  'requests.markAnswered': 'Mark answered',
  'requests.answeredAt': ({ when }: { when: string }) => `Answered ${when}`,
  'requests.openCount': ({ count }: { count: number }) =>
    count === 1 ? '1 message to answer' : `${String(count)} messages to answer`,
  'requests.tentative': 'Requested',

  /* ---------------------------------------------------------------- */
  /* Calendar                                                          */
  /* ---------------------------------------------------------------- */

  'calendar.closedToday': 'Closed on this day.',
  'calendar.noHours': 'No opening hours are set for this day yet — see Settings.',
  'calendar.bookAt': ({ resource, day }: { resource: string; day: string }) =>
    `Book ${resource} on ${day}`,
  'calendar.bookOn': ({ day }: { day: string }) => `Book a time on ${day}`,

  'calendar.previous': 'Earlier',
  'calendar.next': 'Later',
  'calendar.pickDay': 'Go to a date',
  'calendar.today': 'Today',
  'calendar.viewLabel': 'How much to show',
  'calendar.dayView': 'Day',
  'calendar.weekView': 'Week',
  'calendar.whichResource': 'Which room or person',
  'calendar.allResources': 'Everyone',
  'calendar.deskMode': 'Desk screen',
  'calendar.exitDesk': 'Leave desk screen',
  'calendar.closed': 'Closed',

  /**
   * The keyboard path, stated on the screen rather than discovered. `minutes` is
   * the tenant's own `slot_grid_min`, so the sentence names the step this house
   * actually books on instead of a number this app chose.
   */
  'calendar.moveHint': ({ minutes }: { minutes: number }) =>
    `Drag an appointment to move it — or focus one and use the arrow keys (${String(minutes)} min a step), then Enter to confirm.`,

  'calendar.blockLabel': ({
    name,
    service,
    clock,
    minutes,
  }: {
    name: string;
    service: string;
    clock: string;
    minutes: number;
  }) => `${name}, ${service}, ${clock}, ${String(minutes)} min`,
  'calendar.gapAfter': ({ minutes }: { minutes: number }) =>
    `${String(minutes)} min kept after this one`,
  'calendar.companionLabel': ({
    name,
    service,
    clock,
  }: {
    name: string;
    service: string;
    clock: string;
  }) => `${name}, ${service}, ${clock} — this room is taken for it`,
  'calendar.ghostClash': ({ name, clock }: { name: string; clock: string }) =>
    `Runs into ${name} at ${clock}`,

  'calendar.moveDialog': 'Move this appointment',
  'calendar.moveTitle': 'Move it?',
  'calendar.moveQuestion': ({
    name,
    service,
    when,
  }: {
    name: string;
    service: string;
    when: string;
  }) => `Move ${name}’s ${service} to ${when}?`,
  /**
   * **What a move does not do.** A reschedule tells the owner and the partner;
   * it does not text the guest, and a sheet that said it did would be promising
   * a message nobody sends.
   */
  'calendar.moveNotTexted': ({ minutes }: { minutes: number }) =>
    `Still ${String(minutes)} min. She is not texted about a move — that one is yours to send.`,
  'calendar.moveClash': ({ name, clock }: { name: string; clock: string }) =>
    `This device already has ${name} at ${clock} in that room. The cloud decides; you will get a task if it refuses.`,
  'calendar.move': 'Move it',

  'calendar.proposeTitle': 'Offer another time?',
  'calendar.proposeQuestion': ({
    name,
    service,
    when,
  }: {
    name: string;
    service: string;
    when: string;
  }) => `Offer ${name} ${when} for her ${service} instead?`,
  'calendar.proposeTexts': 'It stays a request until she accepts, and she is texted the new time.',
  'calendar.propose': 'Offer it',

  /* S1 — the bottom edge: "this one ran long." */
  'calendar.lengthLabel': ({ name, minutes }: { name: string; minutes: number }) =>
    `Change how long ${name} takes — ${String(minutes)} min now`,
  'calendar.ghostLength': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  /** A resize snapping to one of her lengths: the length and its menu price. */
  'calendar.ghostLengthPriced': ({ minutes, price }: { minutes: number; price: string }) =>
    `${String(minutes)} min · ${price}`,
  'calendar.lengthOptionNote': ({
    was,
    option,
    price,
  }: {
    was: number;
    option: string;
    price: string;
  }) =>
    `It was ${String(was)} min. The start does not move. It is now sold as “${option}”, at ${price} — her menu price.`,
  'calendar.lengthTitle': 'Change the length?',
  'calendar.lengthQuestion': ({
    name,
    service,
    minutes,
  }: {
    name: string;
    service: string;
    minutes: number;
  }) => `Make ${name}’s ${service} ${String(minutes)} min?`,
  'calendar.lengthNote': ({ was }: { was: number }) =>
    `It was ${String(was)} min. The start does not move, and the price does not change — charge for the extra time at the till if you want to.`,
  'calendar.lengthApply': 'Change it',

  /* ---------------------------------------------------------------- */
  /* Tasks (spec §8.3)                                                 */
  /* ---------------------------------------------------------------- */

  'tasks.nothingTitle': 'Nothing needs you',
  'tasks.nothingBody':
    'Everything this iPad has been told has landed in the cloud. Anything that collides while you are offline will show up here.',
  'tasks.heading': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'thing needs you', 'things need you')}`,
  'tasks.findAnother': 'Find another time',
  'tasks.dismiss': 'Dismiss',
  'tasks.didNotGoThrough': ({ what }: { what: string }) => `${what} did not go through`,

  /* "Your data" — a guest's export or deletion request (spec §7.6, GS5). */
  'tasks.data.deleteTitle': ({ name }: { name: string }) => `${name} asked to be forgotten`,
  'tasks.data.exportTitle': ({ name }: { name: string }) =>
    `${name} asked for a copy of their data`,
  'tasks.data.asked': ({ clock, days }: { clock: string; days: number }) =>
    days < 0
      ? `Asked at ${clock} — ${String(-days)} ${plural(-days, 'day', 'days')} overdue.`
      : `Asked at ${clock} — ${String(days)} ${plural(days, 'day', 'days')} left to answer.`,
  'tasks.data.deleteBody':
    'Running this erases their name, phone, email, notes and preferences. Bookings and sales stay on the books without a name. It cannot be undone.',
  'tasks.data.exportBody':
    'Running this prepares one file of everything held about them, for you to hand over. Nothing is emailed.',
  'tasks.data.runDelete': 'Forget this guest',
  'tasks.data.runExport': 'Prepare the file',
  'tasks.data.ownerOnly': 'Only the owner can run this.',
  'tasks.data.forgotten': ({
    identities,
    preferences,
  }: {
    identities: number;
    preferences: number;
  }) =>
    `Done. ${String(identities)} ${plural(identities, 'identity', 'identities')} and ${String(preferences)} ${plural(preferences, 'preference', 'preferences')} erased; the ledger is untouched.`,
  'tasks.data.exported': ({ name }: { name: string }) =>
    `${name}’s file is ready. Save it and hand it over; it is not kept here.`,
  'tasks.data.saveFile': 'Save the file',

  /**
   * The spec's sentence, and its **honesty rule**: when a fact is missing the
   * sentence loses that clause rather than inventing one. That is why the taker
   * clauses branch on `who` inside the entry instead of taking a pre-built tail
   * — a translation has to be free to move "for Marisol Vega" to wherever the
   * language wants it, and in Spanish that is not where English puts it.
   */
  'task.subjectThatTime': 'That time',
  'task.subjectThisTime': ({ when }: { when: string }) => `This ${when}`,
  'task.takenBySomeone': ({ subject }: { subject: string }) =>
    `${subject} was taken while you were offline.`,
  'task.takenByPartner': ({ subject, who }: { subject: string; who: string | null }) =>
    who === null
      ? `${subject} was booked by the concierge while you were offline.`
      : `${subject} was booked by the concierge for ${who} while you were offline.`,
  'task.takenByWalkIn': ({ subject, who }: { subject: string; who: string | null }) =>
    who === null
      ? `${subject} was given to a walk-in while you were offline.`
      : `${subject} was given to a walk-in for ${who} while you were offline.`,
  'task.takenByImport': ({ subject, who }: { subject: string; who: string | null }) =>
    who === null
      ? `${subject} was already in the imported book while you were offline.`
      : `${subject} was already in the imported book for ${who} while you were offline.`,
  'task.takenByGuestWeb': ({ subject, who }: { subject: string; who: string | null }) =>
    who === null
      ? `${subject} was booked on the website while you were offline.`
      : `${subject} was booked on the website for ${who} while you were offline.`,
  'task.takenByStaff': ({ subject, who }: { subject: string; who: string | null }) =>
    who === null
      ? `${subject} was booked at the counter while you were offline.`
      : `${subject} was booked at the counter for ${who} while you were offline.`,
  'task.askPickAnother': 'Pick another time?',
  'task.askRebook': ({ person }: { person: string }) => `Rebook ${person}?`,
  'task.askReschedule': ({ person }: { person: string }) => `Reschedule ${person}?`,

  'task.plainUnknown': 'This did not go through.',
  /**
   * `message` is the **server's own sentence** and is spliced in unchanged; what
   * this catalog adds is the clause that turns a status code into something an
   * owner can act on.
   */
  'task.plainNotFound': ({ message }: { message: string }) =>
    `${message} It may have been cancelled or deleted while you were offline.`,
  'task.plainForbidden': ({ message }: { message: string }) =>
    `${message} This device is signed in to a different business than the one this change was for.`,
  'task.plainUnsupported': ({ message }: { message: string }) =>
    `${message} That part of the app is switched off for this business.`,

  /* ---------------------------------------------------------------- */
  /* The intents, as the Tasks list describes them (actions.ts)        */
  /* ---------------------------------------------------------------- */

  'action.book': ({ client, service }: { client: string; service: string }) =>
    `${client} — ${service}`,
  'action.reschedule': ({ client, service }: { client: string; service: string }) =>
    `Moving ${client} — ${service}`,
  /* S1 — the other half of a drag: the bottom edge, not the body. */
  'action.retime': ({
    client,
    service,
    minutes,
  }: {
    client: string;
    service: string;
    minutes: number;
  }) => `Making ${client}’s ${service} ${String(minutes)} min`,
  'action.cancel': ({ name }: { name: string }) => `Cancelling ${name}`,
  'action.markDone': ({ name }: { name: string }) => `Marking ${name} done`,
  'action.markNoShow': ({ name }: { name: string }) => `Marking ${name} a no-show`,
  'action.addClient': ({ name }: { name: string }) => `Adding ${name}`,
  'action.editClient': ({ name }: { name: string }) => `Editing ${name}`,
  'action.archiveClient': ({ name }: { name: string }) => `Archiving ${name}`,
  'action.restoreClient': ({ name }: { name: string }) => `Restoring ${name}`,
  'action.addContact': ({ name }: { name: string }) => `Adding a contact for ${name}`,
  'action.removeContact': ({ name }: { name: string }) => `Removing a contact for ${name}`,
  'action.notePreference': ({ label, name }: { label: string; name: string }) =>
    `Noting "${label}" for ${name}`,
  'action.rememberDuration': ({
    minutes,
    name,
    service,
  }: {
    minutes: number;
    name: string;
    service: string;
  }) => `Remembering ${String(minutes)} min for ${name}’s ${service}`,
  'action.forgetDuration': ({ name, service }: { name: string; service: string }) =>
    `Back to the usual length for ${name}’s ${service}`,
  'action.removePreference': ({ label, name }: { label: string; name: string }) =>
    `Removing "${label}" from ${name}`,
  'action.addService': ({ name }: { name: string }) => `Adding ${name} to the menu`,
  'action.editService': ({ name }: { name: string }) => `Editing ${name}`,
  /* S2 — the team, as the Tasks list would name a rejected one. */
  'action.addPerson': ({ name }: { name: string }) => `Adding ${name} to the team`,
  'action.addRoom': ({ name }: { name: string }) => `Adding ${name}`,
  'action.editPerson': ({ name }: { name: string }) => `Editing ${name}`,
  'action.timeOff': ({ name }: { name: string }) => `Time off for ${name}`,
  'action.setHours': 'Changing opening hours',
  'action.closeCalendar': 'Closing the calendar',
  'action.closeFor': ({ reason }: { reason: string }) => `Closing for ${reason}`,
  'action.reopen': ({ what }: { what: string }) => `Reopening ${what}`,
  'action.theCalendar': 'the calendar',
  'action.quickSale': ({ name }: { name: string }) => `Quick sale — ${name}`,
  'action.walkIn': 'walk-in',
  'action.checkout': ({ name }: { name: string }) => `Checkout — ${name}`,
  'action.guest': 'guest',
  /** C1 → B4: the shelf's half of a sale, as the task list would name it. */
  'action.collectReservation': ({ name }: { name: string }) => `Pickup order collected — ${name}`,
  /* V5 — the desk sells what the site sells. */
  'action.bookStay': ({ client, room }: { client: string; room: string }) =>
    `Stay for ${client} — ${room}`,
  'action.sellSeat': ({ client, trip }: { client: string; trip: string }) =>
    `Seat for ${client} — ${trip}`,
  'action.addCredit': ({ name }: { name: string }) => `Adding credit for ${name}`,
  'action.giveCredit': ({ name }: { name: string }) => `Giving credit to ${name}`,
  'action.addProduct': ({ name }: { name: string }) => `Adding ${name} to the shelf`,
  'action.editProduct': ({ name }: { name: string }) => `Editing ${name}`,

  /* ---------------------------------------------------------------- */
  /* Clients                                                           */
  /* ---------------------------------------------------------------- */

  'clients.search': 'Search clients',
  'clients.new': 'New',
  'clients.noneMatch': 'No one matches.',
  'clients.noneMatchHeart': ({ button }: { button: string }) => ` Add someone with ${button}.`,
  'clients.noContact': 'no contact',
  'clients.visits': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'visit', 'visits')}`,
  'clients.archived': 'archived',
  'clients.pickSomeone': 'Pick someone.',
  'clients.archive': 'Archive',
  'clients.contactHeading': 'Contact',
  'clients.noIdentity': 'No phone or email — this client cannot be booked until one is added.',
  'clients.preferencesHeading': 'Preferences and allergies',
  'clients.categoryPreference': 'preference',
  'clients.categoryAllergy': 'allergy',
  'clients.staffOnly': 'staff only',
  'clients.staffOnlyTitle': 'Never shown to the booking agent.',
  'clients.nothingNoted': 'Nothing noted yet.',
  'clients.addPreference': 'Add a preference or allergy',
  'clients.kindPhone': 'phone',
  'clients.kindEmail': 'email',
  'clients.addIdentity': 'Add a phone or email',
  'clients.newClient': 'New client',
  'clients.phone': 'Phone',

  /* Her lengths (A1) — how long a service takes this one guest. */
  'clients.lengthsHeading': 'Her lengths',
  /** The service and the length it is sold in, when the row names one (0048). */
  'clients.lengthOption': ({ service, option }: { service: string; option: string }) =>
    `${service} · ${option}`,
  'clients.lengthHers': ({ minutes, catalog }: { minutes: number; catalog: number }) =>
    `${String(minutes)} min · normally ${String(catalog)} min`,
  'clients.lengthForgotten': ({ catalog }: { catalog: number }) =>
    `back to the usual ${String(catalog)} min`,
  'clients.lengthForget': 'Forget',
  'clients.lengthsNone': 'Nothing different — every service takes the usual time.',
  'clients.lengthsNote':
    'A length here changes what is offered from now on. Appointments already booked keep the time they were booked for.',
  'clients.lengthAddService': 'Add a length for…',
  /** The second picker, drawn only when the service is sold in more than one length. */
  'clients.lengthWhich': 'Which length',
  /** Its first entry: the broad row — this service however it is sold. */
  'clients.lengthHowever': 'However it is sold',
  'clients.lengthMinutes': 'Minutes',

  /* What she has bought (B3). */
  'clients.purchasesHeading': 'Purchases',
  'clients.purchasesLoading': 'Looking…',
  'clients.purchasesOffline': 'Purchases need a connection. This one is not in the mirror.',
  'clients.purchasesStale': 'Offline — this is the last list that came through.',
  'clients.purchasesNone': 'Nothing bought yet.',
  'clients.purchasesMore': 'Show more',
  /* Refunds (migration 0046) — started from the purchases list. */
  'clients.refund': 'Refund',
  'clients.purchaseTip': ({ amount }: { amount: string }) => `tip ${amount}`,
  'clients.purchaseRefunded': ({ amount }: { amount: string }) => `${amount} refunded`,
  'refund.dialogLabel': 'Refund',
  'refund.title': 'Give money back',
  'refund.loading': 'Reading what is left on this sale…',
  'refund.needsConnection':
    'A refund needs a connection: only the server knows what has already gone back.',
  'refund.sale': ({ date, total, left }: { date: string; total: string; left: string }) =>
    `${date} · ${total} · ${left} left to refund`,
  'refund.amount': 'Amount',
  'refund.amountHint': 'Leave it blank to give back everything that is left.',
  'refund.tip': ({ left }: { left: string }) => `Tip to return (${left} left)`,
  'refund.howBack': 'How did it go back?',
  'refund.recordedOnly': 'Recorded, never processed — nothing is refunded by this app.',
  'refund.tender.cash': 'cash',
  'refund.tender.check': 'check',
  'refund.tender.venmo': 'Venmo',
  'refund.tender.paypal': 'PayPal',
  'refund.tender.card': 'card',
  'refund.tender.other': 'other',
  'refund.tender.credit_cash': 'account credit',
  'refund.tender.credit_gift': 'gift credit',
  'refund.labelLabel': 'What was it?',
  'refund.labelPlaceholder': 'Zelle, bank transfer…',
  'refund.restock': 'Back on the shelf',
  'refund.restockHint': 'Only tick this if the items can be sold again.',
  'refund.reason': 'Why',
  'refund.tooMuch': 'That is more than this sale has left to give back.',
  'refund.give': 'Record the refund',
  'refund.giving': 'Recording…',
  'refund.failed': 'That refund was not recorded.',
  'refund.alreadyBack': ({ amount }: { amount: string }) => `${amount} already refunded`,
  /* The tips report (migration 0046). */
  'reports.tips': 'Tips',
  'reports.tipsCollected': ({ amount }: { amount: string }) => `${amount} collected`,
  'reports.tipsRefunded': ({ amount }: { amount: string }) => `${amount} given back`,
  'reports.tipsUnattributed': ({ amount }: { amount: string }) => `${amount} unattributed`,
  'reports.tipsNobody': 'No tips in this window.',
  'reports.taxTile': 'Tax collected',
  'reports.refundsTile': 'Refunded',
  'reports.taxCollected': ({ amount }: { amount: string }) => `${amount} tax collected`,
  'reports.refundedOut': ({ amount, n }: { amount: string; n: number }) =>
    `${amount} refunded across ${String(n)}`,

  /* ---------------------------------------------------------------- */
  /* Permissions — the group (ui/Permissions.tsx)                      */
  /*                                                                   */
  /* Three waves each put one tick on the client record on the same    */
  /* day, in three different registers. These words are the group they */
  /* belong in: one shape per row — tick, why, since when — and one    */
  /* sentence where a permission cannot apply at all.                  */
  /*                                                                   */
  /* The verb is the same on all three ("Text her…", "Email her…",     */
  /* "Let her…"): an imperative to the person holding the iPad, about  */
  /* a thing she is about to promise somebody, rather than a setting   */
  /* describing a system.                                              */
  /* ---------------------------------------------------------------- */

  'permissions.heading': 'Permissions',
  'permissions.note': 'What she has agreed to, and what you have allowed her to do.',

  'permissions.newsletterTick': 'Email her our newsletter',
  'permissions.newsletterOff': 'Off — she gets no newsletter.',
  'permissions.newsletterSince': ({ day, how }: { day: string; how: string }) =>
    `Since ${day} — ${how}`,
  /** 0042's row, before anybody ticked anything. Never a guessed date. */
  'permissions.newsletterSinceUnknown': 'On from before we started recording when or how.',
  'permissions.noEmail': 'No email address — she cannot be sent the newsletter until one is added.',
  'permissions.emailHowStaff': 'you ticked it',
  'permissions.emailHowGuestSite': 'she signed up on the website',
  'permissions.emailHowUnsubscribe': 'she used the unsubscribe link',
  'permissions.emailHowBounce': 'her mailbox stopped accepting mail',
  'permissions.emailHowImport': 'it came in with an import, which recorded nothing',
  'permissions.emailHowAnonymized': 'the guest asked to be forgotten and the request was run',

  'permissions.bookOnlineTick': 'Let her book herself online',
  /**
   * Two sentences and both are load-bearing. The first says what the tick does
   * *and when it does nothing*, so ticking it on an `open` tenant is a recorded
   * decision rather than a mystery. The second is the honest absence: migration
   * 0040 gave this column no when-and-how pair, so there is no since-when line
   * to print and pretending otherwise would be the exact failure 0041 exists to
   * end.
   */
  'permissions.bookOnlineNote':
    'Only matters when your website asks strangers to request a time — then she books instead of asking. We do not record when this was granted.',

  /* ---------------------------------------------------------------- */
  /* Consent to be texted (§2.6.1(a), migration 0041)                  */
  /* ---------------------------------------------------------------- */

  /**
   * A promise, not a setting — `ui/SmsReminders.tsx` argues why. `sms.ask` and
   * `sms.script` are two entries rather than one sentence because only the
   * second is **what a staff member says out loud**, and a translator must be
   * able to move the framing without touching the words that are quoted.
   */
  'sms.heading': 'Reminders',
  'sms.tick': 'Text her appointment reminders',
  'sms.ask': 'Ask her:',
  'sms.script': 'Is it OK if we text you a reminder the evening before?',
  'sms.off': 'Off — she gets no reminders.',
  'sms.since': ({ day, how }: { day: string; how: string }) => `Since ${day} — ${how}`,
  'sms.howStaff': 'you ticked it',
  'sms.howGuestSite': 'she asked on the website',
  'sms.howReplyYes': 'she replied YES by text',
  'sms.howReplyStop': 'she replied STOP',
  'sms.howImport': 'it came in with an import, which recorded nothing',
  'sms.howAnonymized': 'the guest asked to be forgotten and the request was run',
  /** The pre-0041 row. It says what it does not know rather than guessing. */
  'sms.sinceUnknown': 'On from before we started recording when or how.',
  'sms.noPhone': 'No phone number — she cannot be texted until one is added.',

  /* ---------------------------------------------------------------- */
  /* Account credit (§7.4 rule 2)                                      */
  /* ---------------------------------------------------------------- */

  'credit.heading': 'On account',
  'credit.cash': 'Cash credit',
  'credit.cashHint': ' — money paid in; a payment when spent',
  'credit.gift': 'Gift credit',
  'credit.giftHint': ' — goodwill; a discount when spent, never income',
  'credit.amountLabel': 'Credit amount',
  'credit.amountPlaceholder': 'Amount',
  'credit.reasonLabel': 'Credit reason',
  'credit.reasonPlaceholder': 'Why',
  'credit.moneyIn': 'Money in',
  'credit.goodwill': 'Goodwill',
  'credit.kindCash': 'cash',
  'credit.kindGift': 'gift',
  'credit.reasonTail': ({ reason }: { reason: string }) => ` · ${reason}`,
  'credit.spentAtCheckout': ' · spent at checkout',
  'credit.empty': 'Nothing on account, and nothing spent.',

  /* ---------------------------------------------------------------- */
  /* Retail (§7.3.1)                                                   */
  /* ---------------------------------------------------------------- */

  'products.heading': 'Shelf',
  'products.brandTail': ({ brand }: { brand: string }) => ` · ${brand}`,
  'products.empty': 'Nothing on the shelf yet.',
  'products.nameLabel': 'Product name',
  'products.priceLabel': 'Product price',
  'products.brand': 'Brand',
  'products.add': 'Add product',

  /* ---------------------------------------------------------------- */
  /* Photos of a product or a treatment (B7, `ItemPhotos.tsx`)          */
  /* ---------------------------------------------------------------- */

  'photos.heading': 'Photos',
  'photos.none': 'No photos yet.',
  'photos.needsConnection':
    'Photos go straight onto your website, so adding or changing them needs a connection. ' +
    'Nothing is saved to send later.',
  'photos.offline': 'This iPad is offline. Connect to add or change photos.',
  'photos.choose': 'Add photos',
  'photos.altLabel': 'What is in this photo?',
  'photos.altPlaceholder': ({ name }: { name: string }) => `e.g. ${name} on the counter`,
  'photos.altRequired':
    'Describe each photo before uploading. It is what a screen reader says on your website.',
  'photos.upload': ({ count }: { count: number }) =>
    `Upload ${String(count)} ${plural(count, 'photo', 'photos')}`,
  'photos.discard': 'Don’t add',
  'photos.waiting': 'waiting',
  'photos.percent': ({ percent }: { percent: number }) => `${String(percent)}%`,
  'photos.failed': 'failed — try again',
  'photos.moveEarlier': ({ alt }: { alt: string }) => `Move “${alt}” earlier`,
  'photos.moveLater': ({ alt }: { alt: string }) => `Move “${alt}” later`,
  'photos.remove': ({ alt }: { alt: string }) => `Remove “${alt}”`,
  'photos.removeConfirm': 'Take this photo off your website?',
  'photos.removeYes': 'Take it off',
  'photos.keep': 'Keep it',
  'photos.full': ({ max }: { max: number }) =>
    `${String(max)} photos is the most one item can have. Remove one to add another.`,
  /* Per photo: on the website or not (B10). */
  'photos.onWebsite': 'On the website',
  'photos.offWebsite': 'Not on the website',
  'photos.onWebsiteFor': ({ alt }: { alt: string }) => `“${alt}” on the website`,

  /* ---------------------------------------------------------------- */
  /* What goes on the website, and who may change it (B10)             */
  /* ---------------------------------------------------------------- */

  'catalog.showOnWebsite': 'Show on the website',
  'catalog.offWebsite': 'not on the website',
  'catalog.offWebsiteTitle': 'Still sold here; guests do not see it on the website.',
  'catalog.readOnlyStaff':
    'Only the owner changes what the business sells. The owner can allow staff to in Settings.',
  'catalog.readOnlySeat': 'This login can see what the business sells but not change it.',
  'catalog.details': 'Details',
  'catalogEditors.heading': 'Who may change what we sell',
  'catalogEditors.hint':
    'Adding, editing and taking products and services off sale, their photos, and what shows on the website.',
  'catalogEditors.owner': 'Only the owner',
  'catalogEditors.ownerAndStaff': 'The owner and staff',
  'catalogEditors.readonlyNote': 'Read-only logins never can.',
  'catalogEditors.saved': 'Saved.',

  /* ---------------------------------------------------------------- */
  /* Settings                                                          */
  /* ---------------------------------------------------------------- */

  'settings.menu': 'Menu',
  'settings.serviceLine': ({ minutes, price }: { minutes: number; price: string }) =>
    `${String(minutes)} min · ${price}`,
  'settings.optionLength': ({ minutes, price }: { minutes: number; price: string }) =>
    `${String(minutes)} min ${price}`,
  'settings.optionNamed': ({ name, price }: { name: string; price: string }) => `${name} ${price}`,
  'settings.retiredCount': ({ count }: { count: number }) =>
    count === 1 ? '1 retired entry' : `${String(count)} retired entries`,
  'settings.buffer': ({ minutes }: { minutes: number }) => ` · +${String(minutes)} min buffer`,
  /* The gap either side of a treatment (0064): blank = the house's, 0 = none. */
  'settings.bufferFromHouse': ' (house default)',
  'settings.bufferBefore': 'Gap before (min)',
  'settings.bufferAfter': 'Gap after (min)',
  'settings.houseDefault': ({ minutes }: { minutes: number }) =>
    `house default (${String(minutes)})`,
  'settings.counterOnly': 'counter only',
  'settings.counterOnlyTitle': "Kept off the booking agent's menu.",
  'settings.minutes': 'Minutes',
  'settings.addService': 'Add service',
  'settings.agentMayBook': 'the agent may book it',
  'settings.openingHours': 'Opening hours',
  'settings.closed': 'closed',
  'settings.addWindow': '+ window',
  'settings.saveWeek': 'Save the week',
  'settings.closures': 'Closures',
  'settings.blackoutSpan': ({ from, to }: { from: string; to: string }) => `${from} – ${to}`,
  'settings.blackoutReason': ({ reason }: { reason: string }) => ` · ${reason}`,
  'settings.nothingClosed': 'Nothing closed.',
  'settings.reason': 'Reason',
  'settings.closeThisTime': 'Close this time',
  'settings.thisDevice': 'This device',
  'settings.business': 'Business',
  'settings.timezone': 'Timezone',
  'settings.status': 'Status',
  'settings.signedInAs': 'Signed in as',
  'settings.switchedOn': 'Switched on',
  'settings.mayChange': 'May change the book',
  'settings.canWriteYes': 'yes',
  'settings.canWriteNo': 'no — this login can read only',

  /* ---------------------------------------------------------------- */
  /* The team (S2)                                                     */
  /* ---------------------------------------------------------------- */

  'team.title': 'Team',
  /**
   * The solo card. Two sentences and no vocabulary: an owner working alone is
   * never told what a "resource" is, and the lane the importer made for her is
   * mentioned once, in passing, so the word on her calendar has an explanation
   * if she goes looking for one.
   */
  'team.justYouTitle': 'It’s just you',
  'team.justYouBody':
    'Nothing to keep track of here yet. Your week is under Settings, ' +
    'and the day you take somebody on, add them here.',
  'team.soloLane': ({ name }: { name: string }) => `Your calendar keeps one lane, “${name}”.`,
  'team.people': 'People',
  'team.nobodyYet': 'Nobody yet.',
  'team.addPerson': 'Add someone',
  'team.rooms': 'Rooms and equipment',
  'team.noRooms': 'No rooms yet.',
  'team.addRoom': 'Add a room',
  'team.units': ({ units }: { units: number }) =>
    `${String(units)} interchangeable ${plural(units, 'one', 'ones')}`,
  'team.order': 'Order',
  'team.login': 'Login',
  'team.noLogin': 'no login',
  'team.signsInAs': ({ email }: { email: string }) => ` · signs in as ${email}`,
  'team.someLogin': 'a login on this device’s list',
  'team.followsHouse': 'Follows the business’s hours',
  'team.ownWeek': 'Works their own week',
  'team.giveOwnWeek': 'Give them their own week',
  'team.backToHouse': 'Back to the business’s hours',
  'team.notWorking': 'not working',
  'team.worksNoDays': 'No working days set',
  'team.timeOff': 'Time off',
  'team.noTimeOff': 'None booked.',
  'team.bookTimeOff': 'Book time off',
  /**
   * The conflicts. Worded as a question for the *owner* rather than as an
   * error, because nothing failed: the time off is booked, and these are the
   * calls she now has to make. Nothing on the screen offers to make them for
   * her — spec §8.3.
   */
  'team.conflictsHeading': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'booking is', 'bookings are')} inside this — ` +
    'move or cancel them yourself.',
  'team.conflictLine': ({ when, who, what }: { when: string; who: string; what: string }) =>
    `${when} · ${who} · ${what}`,
  'team.whoDoesWhat': 'Who does what',
  'team.whoDoesWhatHint':
    'A tick means that person or room can deliver the service. ' +
    'A service with no ticks cannot be booked at all.',
  'team.service': 'Service',
  'team.matrixCell': ({ service, who }: { service: string; who: string }) =>
    `${who} does ${service}`,
  'team.needsBoth': 'Needs one person and one room',
  'team.needsBothCell': ({ service }: { service: string }) =>
    `${service} needs one person and one room`,
  'team.needsBothHint':
    'Ticked, the service takes one of its people and one of its rooms together — ' +
    'a person is offered only while a room is free, and a room only while a person is.',

  /* ---------------------------------------------------------------- */
  /* Gift certificates (§7.3)                                          */
  /* ---------------------------------------------------------------- */

  'certificates.faceValue': 'At face value',
  'certificates.expiredUnspent': 'Expired unspent',
  'certificates.remainingOf': ({ remaining, value }: { remaining: string; value: string }) =>
    `${remaining} of ${value}`,
  'certificates.empty': ({ where }: { where: string }) =>
    `No certificates yet. Issue one ${where}; they are redeemed at checkout, by code — never from this screen.`,
  'certificates.emptyHere': 'here',
  'certificates.emptyWithButton': ({ button }: { button: string }) => `with ${button}`,
  'certificates.needAmount': 'A certificate needs an amount or a service.',
  /*
   * Gift requests (owner's ruling 1, migration 0051) - the promises the desk
   * settles. **Every word here says "not paid yet"**, because that is the one
   * fact somebody will get wrong: an unpaid ask is not a liability and is not
   * in the outstanding figure above it (spec 7.4 rule 1).
   */
  'giftRequests.title': 'Gift requests',
  'giftRequests.unpaid':
    'Asked for online and not paid for. Sell the card here and it becomes a certificate.',
  'giftRequests.none': 'Nobody is waiting for a gift card.',
  'giftRequests.offline':
    'This list needs the network. It is a queue of people to ring, so there is no offline copy.',
  'giftRequests.forService': ({ name, price }: { name: string; price: string }) =>
    `${name} — ${price}`,
  'giftRequests.forWhom': ({ name }: { name: string }) => ` · for ${name}`,
  'giftRequests.sell': 'Sell this certificate',
  'giftRequests.whichCard': 'Which card',
  'giftRequests.markSold': 'Mark as sold',
  'giftRequests.cancel': 'Cancel',
  'certificates.issuingAmount': ({ amount }: { amount: string }) =>
    `Issuing a ${amount} certificate`,
  'certificates.issuingService': ({ name }: { name: string }) =>
    `Issuing a certificate for ${name}`,
  'certificates.aService': 'a service',
  'certificates.amountLabel': 'Certificate amount',
  'certificates.amountPlaceholder': 'Amount',
  'certificates.serviceLabel': 'Certificate service',
  /*
   * A card that names a length (migration 0054, the owner's ruling 2). The
   * amount box is prefilled with the catalog's price for that length and this
   * line says so, so a typed change is a decision and not a surprise.
   */
  'certificates.lengthLabel': 'Length',
  'certificates.namedLength': ({ name, length }: { name: string; length: string }) =>
    `${name} · ${length}`,
  'certificates.catalogPrice': ({ amount, length }: { amount: string; length: string }) =>
    `${amount} is the catalog’s price for ${length}. Leave it and the card is worth exactly that; change it to sell for another amount.`,
  'certificates.recipientLabel': 'Recipient',
  'certificates.purchaserLabel': 'Purchaser',
  'certificates.expiresLabel': 'Expires on',
  'certificates.issue': 'Issue',
  /*
   * The Gift cards tab (owner's decisions 1 and 2 of 2026-09-23) — the shape of
   * her old LoveSpa screen: a summary line, a search, four filters with
   * Outstanding first, and a sale that asks how it was paid. "Gift card" is her
   * word; the platform's is certificate, and the platform's stays in the code.
   */
  'giftcards.heading': 'Gift cards',
  'giftcards.new': 'Gift card',
  'giftcards.summary': ({ count, amount }: { count: number; amount: string }) =>
    `${String(count)} outstanding · ${amount} waiting to be cashed`,
  'giftcards.search': 'Search code, name, service…',
  'giftcards.searchLabel': 'Search gift cards',
  'giftcards.filterLabel': 'Show',
  'giftcards.filterOutstanding': 'Outstanding',
  'giftcards.filterRedeemed': 'Redeemed',
  'giftcards.filterExpired': 'Expired',
  'giftcards.filterAll': 'All',
  'giftcards.giftAmount': 'Gift amount',
  'giftcards.whatAndValue': ({ what, value }: { what: string; value: string }) =>
    `${what} · ${value}`,
  'giftcards.forWhom': ({ name }: { name: string }) => `for ${name}`,
  'giftcards.boughtBy': ({ name }: { name: string }) => `bought by ${name}`,
  'giftcards.issued': ({ date }: { date: string }) => `issued ${date}`,
  'giftcards.expires': ({ date }: { date: string }) => `expires ${date}`,
  'giftcards.expired': ({ date }: { date: string }) => `expired ${date}`,
  'giftcards.neverExpires': 'never expires',
  'giftcards.paidWith': ({ tender }: { tender: string }) => `paid by ${tender}`,
  'giftcards.noMatch': 'No gift cards match that search.',
  'giftcards.noneInFilter': 'Nothing here.',
  'giftcards.sheetLabel': 'New gift card',
  'giftcards.amountOption': 'Gift amount (no service)',
  'giftcards.howPaid': 'How was it paid?',
  'giftcards.needTender': 'Say how it was paid before issuing it.',
  /*
   * Who holds it and who bought it (migration 0074): both are clients of the
   * book, picked or added right here. A card is tracked by who holds it, so
   * the recipient is required; the buyer may be somebody off the street.
   */
  'giftcards.walkIn': 'Walk-in / not a client',
  'giftcards.needRecipient':
    'Choose who the card is for — one of your clients, or add her as a new one.',
  /*
   * The code (her complaint: *there is no code on the gift card*). Made on the
   * iPad so it can be read out before there is signal, or typed off a card
   * printed in advance.
   */
  'giftcards.codeLabel': 'Card code',
  'giftcards.codeMinted': 'A code is created when you issue it.',
  'giftcards.codeUsePrinted': 'Use the number printed on the card',
  'giftcards.codeUseMinted': 'Create a code instead',
  'giftcards.codePrintedLabel': 'Number printed on the card',
  'giftcards.codePrintedPlaceholder': 'e.g. GC-3632',
  'giftcards.codePrintedWillBe': ({ code }: { code: string }) => `Saved as ${code}`,
  'giftcards.codePrintedInvalid':
    'Type the number as it is printed — letters, digits and dashes, at least 4 characters.',
  'giftcards.codeTaken': ({ name }: { name: string }) =>
    `That number is already on another gift card${name}. Check the card, or let the app create a code.`,
  'giftcards.codeTakenFor': ({ name }: { name: string }) => ` (for ${name})`,
  /* After Issue: the card, read back to the buyer. */
  'giftcards.issuedHeading': 'Gift card issued',
  'giftcards.notIssuedHeading': 'Not issued',
  'giftcards.codeAria': ({ code }: { code: string }) => `Card code ${code}`,
  'giftcards.copy': 'Copy code',
  'giftcards.copied': 'Copied.',
  'giftcards.copySelected': 'The code is selected — copy it from the menu.',
  'giftcards.statusPending':
    'Saved on this iPad. It reaches your books as soon as there is signal — the code will not change.',
  'giftcards.statusSaved': 'Saved to your books.',
  'giftcards.refusedCode':
    'That number is already on another gift card, so this one was not issued.',
  'giftcards.refusedClient': 'The client on this card was not found, so it was not issued.',
  'giftcards.refused': ({ message }: { message: string }) => `It was not issued: ${message}`,
  'giftcards.fixIt': 'Go back and fix it',
  'giftcards.factCard': 'Card',
  'giftcards.factFor': 'For',
  'giftcards.factFrom': 'From',
  'giftcards.factExpires': 'Expires',
  'giftcards.factPaid': 'Paid',
  'giftcards.never': 'Never',
  'giftcards.done': 'Done',
  /*
   * The client picker (`ClientPicker.tsx`): search her book by name or phone,
   * or add somebody new without leaving the sale. The new client is queued in
   * front of the card, so it lands first even in a tunnel.
   */
  'clientPicker.search': 'Search name or phone',
  'clientPicker.noMatch': ({ search }: { search: string }) => `No client matches “${search}”.`,
  'clientPicker.new': '+ New client',
  'clientPicker.email': 'Email',
  'clientPicker.change': 'Change',
  'clientPicker.isNew': 'new — added to the book with this',
  'clientPicker.needName': 'Give the new client a name.',
  'clientPicker.needContact': 'Add a phone number or an email, so you can find her again.',
  'clientPicker.badPhone': 'That phone number does not look right.',
  'clientPicker.badEmail': 'That email does not look right.',
  'clientPicker.taken': ({ name }: { name: string }) => `That is already ${name}’s.`,
  'clientPicker.useExisting': ({ name }: { name: string }) => `Use ${name}`,
  'certificates.footnote':
    'Issuing records the sale on today’s date — a certificate is income the day it is sold, and the visit it pays for later carries only the remainder.',

  /* ---------------------------------------------------------------- */
  /* Book (the one booking form)                                       */
  /* ---------------------------------------------------------------- */

  'book.dialogLabel': 'Book an appointment',
  'book.title': 'Book',
  'book.service': 'Service',
  'book.choose': 'Choose…',
  'book.serviceOption': ({ name, minutes }: { name: string; minutes: number }) =>
    `${name} · ${String(minutes)} min`,
  /* The service picker: her menu, under her headings, searchable. */
  'book.findService': 'Search the menu',
  'book.noServiceMatch': ({ query }: { query: string }) => `Nothing on the menu matches “${query}”`,
  'book.changeService': 'Change',
  /** A service sold in several lengths shows what it spans — "$85–$340". */
  'book.priceRange': ({ from, to }: { from: string; to: string }) => `${from}–${to}`,
  'book.where': 'Where',
  'book.who': 'Who',
  'book.room': 'Room',
  'book.errorRoom': 'Choose a room.',
  'book.starts': 'Starts',
  /** "10:45 AM – 12:45 PM": the start she chose and the end the length makes. */
  'book.timeRange': ({ from, to }: { from: string; to: string }) => `${from} – ${to}`,
  /** Only drawn when this device's clock is in another zone than the business. */
  'book.zoneNote': ({ timezone }: { timezone: string }) => `${timezone} time`,
  'book.existingGuest': 'Guest already in the book',
  'book.guest': 'Guest',
  'book.someoneNew': 'Someone new…',
  'book.errorService': 'Choose a service.',
  'book.errorWhere': 'Choose where it happens.',
  'book.errorTime': 'Choose a time.',
  'book.errorName': 'Name the guest.',
  'book.errorContact': 'A phone number or email is needed to book someone new.',
  'book.errorLength': 'A length is a whole number of minutes between 5 and 480.',
  'book.submit': 'Book',
  /** The button says what it will do: "Book 10:45 AM – 12:45 PM". */
  'book.submitRange': ({ range }: { range: string }) => `Book ${range}`,

  /* The length control (A1). */
  'book.length': 'Length',
  /** The number field behind "Adjust time". */
  'book.minutes': 'Minutes',
  'book.lengthMinus': ({ by }: { by: number }) => `${String(by)} minutes shorter`,
  'book.lengthPlus': ({ by }: { by: number }) => `${String(by)} minutes longer`,
  'book.lengthPlain': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  /** A chip: the length and what it costs — "60 min · $170". */
  'book.lengthChip': ({ minutes, price }: { minutes: number; price: string }) =>
    `${String(minutes)} min · ${price}`,
  'book.adjustTime': 'Adjust time',
  'book.adjustReset': 'Undo',
  /**
   * The one line an adjusted length gets: what is booked and what the menu
   * says, so a number that differs from the card is never a silent one.
   */
  'book.lengthBooked': ({ minutes, menu }: { minutes: number; menu: number }) =>
    `${String(minutes)} min booked · the menu says ${String(menu)}`,
  'book.lengthFromClient': ({ minutes, catalog }: { minutes: number; catalog: number }) =>
    `usually ${String(minutes)} min for her · menu ${String(catalog)} min`,
  /**
   * The same sentence for a memory that names a length (migration 0048). The
   * `catalog` here is **that option's** span, not the item's, so the two numbers
   * being compared are about the same thing.
   */
  'book.lengthFromClientOption': ({
    minutes,
    catalog,
    option,
  }: {
    minutes: number;
    catalog: number;
    option: string;
  }) => `usually ${String(minutes)} min for her · ${option}, menu ${String(catalog)} min`,
  'book.rememberLength': ({
    minutes,
    name,
    service,
  }: {
    minutes: number;
    name: string;
    service: string;
  }) => `Remember ${String(minutes)} min for ${name}’s ${service}`,

  /* The ways it is sold, and what may be attached (migration 0048). */
  /**
   * One chip. The price is on it because *"is the 90 two-fifty-five?"* is the
   * question the chips exist to stop somebody asking, and the length is already
   * the chip's name — the owner wrote "90 minutes".
   */
  'book.optionChip': ({ name, price }: { name: string; price: string }) => `${name} · ${price}`,
  'book.addOns': 'Add to this booking',
  'book.addOnChoice': ({
    name,
    price,
    minutes,
  }: {
    name: string;
    price: string;
    minutes: number;
  }) => `${name} · +${String(minutes)} min · ${price}`,
  /**
   * **The summary card** — what is being sold, at the menu's prices (B7):
   * "Custom Deluxe Facial · 120 min · $340", a line per add-on, and a total
   * once there is more than one line. The add-ons' minutes are added by the
   * server too, so the total is also how long the room is actually taken.
   */
  'book.summaryLabel': 'This booking',
  'book.summaryLine': ({
    name,
    minutes,
    price,
  }: {
    name: string;
    minutes: number;
    price: string;
  }) => `${name} · ${String(minutes)} min · ${price}`,
  'book.summaryAddOn': ({
    name,
    minutes,
    price,
  }: {
    name: string;
    minutes: number;
    price: string;
  }) => `+ ${name} · ${String(minutes)} min · ${price}`,
  'book.summaryTotal': ({ minutes, price }: { minutes: number; price: string }) =>
    `Total · ${String(minutes)} min · ${price}`,
  /**
   * A warning, never a refusal — and the wording carries that. The mirror can
   * honestly say "something is booked here"; only `packages/scheduling` can say
   * "this is free", so the sentence claims the first and not the second.
   */
  'book.clash': ({ name, clock }: { name: string; clock: string }) =>
    `This runs into ${name} at ${clock} in the copy of the book on this device. Book it anyway if you know better.`,

  /* ---------------------------------------------------------------- */
  /* The appointment tap (B1) and its note field                       */
  /* ---------------------------------------------------------------- */

  'visit.dialogLabel': 'Visit',
  'visit.headline': ({
    clock,
    service,
    minutes,
  }: {
    clock: string;
    service: string;
    minutes: number;
  }) => `${clock} · ${service} · ${String(minutes)} min`,
  /**
   * The service and the way it was sold, as one phrase inside the headline
   * above (migration 0048): "Custom Deluxe Facial · 90 minutes".
   */
  'visit.serviceOption': ({ service, option }: { service: string; option: string }) =>
    `${service} · ${option}`,
  'visit.addOns': 'Added',
  /** The **attached** price and minutes, snapshotted when it was booked. */
  'visit.addOnLine': ({ price, minutes }: { price: string; minutes: number }) =>
    `${price} · +${String(minutes)} min`,
  'visit.allergies': 'Allergies',
  'visit.preferences': 'Preferences',
  'visit.lastVisit': 'Last visit',
  'visit.lastVisitLine': ({ day, service }: { day: string; service: string }) =>
    `${day} — ${service}`,
  /**
   * Not "never". The mirror holds a window of the book, so an empty answer is a
   * statement about this device and is written as one.
   */
  'visit.noRecentVisit': 'Nothing in the weeks this device holds.',
  'visit.herLength': ({
    service,
    minutes,
    catalog,
  }: {
    service: string;
    minutes: number;
    catalog: number;
  }) => `Her ${service} usually takes ${String(minutes)} min · menu ${String(catalog)} min`,
  'visit.notes': 'Notes',
  'visit.noNotes': 'Nothing written down yet.',
  'visit.purchases': 'Purchases',
  'visit.birthdayToday': 'Birthday today',
  'visit.birthdayTomorrow': 'Birthday tomorrow',
  'visit.birthdaySoon': ({ days }: { days: number }) =>
    `Birthday in ${String(days)} ${plural(days, 'day', 'days')}`,

  'note.add': 'Add a note',
  'note.placeholder': 'Anything worth remembering',
  'note.dictate': 'Speak it',
  'note.listening': 'Listening…',

  /* ---------------------------------------------------------------- */
  /* Checkout (§6.5)                                                   */
  /* ---------------------------------------------------------------- */

  'checkout.dialogLabel': 'Checkout',
  'checkout.quickSale': 'Quick sale',
  'checkout.closeOut': ({ name }: { name: string }) => `Close out — ${name}`,
  'checkout.guestFallback': 'guest',
  'checkout.guest': 'Guest',
  'checkout.listAndOff': ({ off }: { off: string }) => ` list · ${off} off each`,
  'checkout.qtyLabel': ({ name }: { name: string }) => `Quantity of ${name}`,
  'checkout.priceLabel': ({ name }: { name: string }) => `Price of ${name}`,
  /**
   * The till is showing a pickup order's lines (C1 → B4). Said out loud because
   * the prices on them are the ones the guest was **quoted on the website**, not
   * necessarily today's catalog: if the shelf moved in between, the line reads
   * as an ordinary discount or upcharge against the list price, and a cashier
   * who cannot see why deserves the sentence.
   */
  'checkout.fromReservation': ({ name }: { name: string }) =>
    `From ${name}'s pickup order — at the price they were quoted.`,
  'checkout.reservationLineMissing':
    'One line named a product that is no longer in the catalog and was left off. Add it by hand.',
  'checkout.discountLabel': 'Discount',
  'checkout.certCodeLabel': 'Certificate code',
  'checkout.certCodePlaceholder': 'Code',
  'checkout.lookUp': 'Look up',
  'checkout.noSuchCode': 'No certificate with that code.',
  'checkout.certExpired': ({ date }: { date: string }) => `That certificate expired ${date}.`,
  'checkout.certEmpty': 'That certificate has nothing left on it.',
  'checkout.certLeft': ({ code, amount }: { code: string; amount: string }) =>
    `${code} · ${amount} left`,
  'checkout.certItem': ({ name }: { name: string }) => ` · ${name}`,
  'checkout.certAmountLabel': 'Certificate amount',
  'checkout.certAmountPlaceholder': 'all of it',
  'checkout.useDepositLabel': 'Use the deposit',
  'checkout.depositTaken': ({ amount }: { amount: string }) => `${amount} taken already`,
  'checkout.depositUnknown': 'whatever is left of the deposit on file',
  'checkout.useCreditLabel': 'Use account credit',
  'checkout.creditPools': ({ gift, cash }: { gift: string; cash: string }) =>
    `${gift} gift (spent first, as a discount) · ${cash} cash`,
  'checkout.giftAmountLabel': 'Gift credit amount',
  'checkout.giftAmountPlaceholder': 'gift: all',
  'checkout.cashAmountLabel': 'Cash credit amount',
  'checkout.cashAmountPlaceholder': 'cash: all',
  /*
   * The six recorded tenders (B4). Lower case, like the three they replace:
   * these are words in a sentence about how a sale was paid, not brand names
   * being advertised — and nothing here is processed, so a logo would be a
   * promise the platform does not make. Keyed `checkout.tender.<kind>` so the
   * button row maps straight off `session.till.tenders` with no lookup table
   * that could fall out of step with the enum.
   */
  'checkout.tender.cash': 'cash',
  'checkout.tender.check': 'check',
  'checkout.tender.venmo': 'Venmo',
  'checkout.tender.paypal': 'PayPal',
  'checkout.tender.card': 'card',
  'checkout.tender.other': 'other',
  'checkout.tenderLabelLabel': 'What was it?',
  'checkout.tenderLabelPlaceholder': 'Zelle, bank transfer…',
  /* Split tender, tips and refunds (migration 0046). */
  'checkout.addPayment': '+ Add another payment',
  'checkout.removePayment': 'Remove',
  'checkout.partAmountLabel': ({ n }: { n: number }) => `Amount for payment ${String(n)}`,
  'checkout.partTenderLabel': ({ n }: { n: number }) => `How payment ${String(n)} was made`,
  'checkout.partRest': 'the rest',
  'checkout.splitHint': 'The last one takes whatever is left.',
  'checkout.tip': 'Tip',
  'checkout.tipLabel': 'Tip amount',
  'checkout.tipHint': 'For the person, not the shop — never counted as revenue.',
  'checkout.tipPercent': ({ percent }: { percent: number }) => `${String(percent)}%`,
  'checkout.tipClear': 'No tip',
  'checkout.tax': 'Tax',
  'checkout.note': 'Note',
  /* The register (B4). */
  'checkout.searchLabel': 'Find an item',
  'checkout.searchPlaceholder': 'Type a few letters…',
  'checkout.searchNoMatch': ({ query }: { query: string }) => `Nothing matches “${query}”.`,
  'checkout.keypadLabel': 'Number pad',
  'checkout.lineNoteLabel': ({ name }: { name: string }) => `What this ${name} was`,
  'checkout.lineNotePlaceholder': 'coral scarf',
  'checkout.doneDialogLabel': 'Sale rung up',
  'checkout.doneTitle': 'Rung up',
  'checkout.doneTender': ({ tender }: { tender: string }) => `· ${tender}`,
  'checkout.doneQueued': 'Queued. It lands as soon as this device has a signal.',
  'checkout.needsALine': 'A settlement needs at least one line.',
  /* The cart (2026-09-30): catalog on one side, this sale on the other. */
  'checkout.walkIn': 'Walk-in',
  'checkout.guestButton': ({ name }: { name: string }) => `Guest: ${name}`,
  'checkout.guestChange': 'Change',
  'checkout.guestSearchLabel': 'Search clients',
  'checkout.guestSearchPlaceholder': 'Name or phone',
  'checkout.guestNoMatch': ({ query }: { query: string }) => `No client matches “${query}”.`,
  'checkout.catalogLabel': 'What you sell',
  'checkout.catalogEmpty': 'Nothing on the menu yet.',
  'checkout.searchResults': 'Matches',
  'checkout.tabServices': 'Services',
  'checkout.tabProducts': 'Products',
  'checkout.moreServices': 'More',
  'checkout.tileService': ({ minutes, price }: { minutes: number; price: string }) =>
    `${price} · ${String(minutes)} min`,
  'checkout.tileLengths': ({ count, price }: { count: number; price: string }) =>
    `from ${price} · ${String(count)} lengths`,
  'checkout.tileAddOn': ({ minutes, price }: { minutes: number; price: string }) =>
    `+${price} · ${String(minutes)} min`,
  'checkout.minutes': ({ minutes }: { minutes: number }) => `${String(minutes)} min`,
  'checkout.priceAtTill': 'price at the till',
  'checkout.chooseLength': ({ name }: { name: string }) => `${name} — which length?`,
  'checkout.addOnsFor': ({ name }: { name: string }) => `Add to ${name}`,
  'checkout.addOnOf': ({ name }: { name: string }) => `on ${name}`,
  'checkout.cartSection': 'This sale',
  'checkout.cartLabel': 'Items on this sale',
  'checkout.cartEmpty': 'Nothing on this sale yet — tap an item to add it.',
  'checkout.lessQty': ({ name }: { name: string }) => `One fewer ${name}`,
  'checkout.moreQty': ({ name }: { name: string }) => `One more ${name}`,
  'checkout.removeLine': ({ name }: { name: string }) => `Remove ${name}`,
  'checkout.priceSheetTitle': ({ name }: { name: string }) => `How much is the ${name}?`,
  'checkout.priceSheetAmount': 'Price',
  'checkout.priceSheetAdd': 'Add to sale',
  'checkout.priceSheetSave': 'Update price',
  'checkout.keypadBackspace': 'Delete',
  'checkout.lineNoteHint': 'What was it? (optional)',
  'checkout.subtotalLabel': 'Subtotal',
  'checkout.addDiscount': 'Add discount',
  'checkout.removeDiscount': 'Remove discount',
  'checkout.addTax': 'Add tax',
  'checkout.removeTax': 'Remove tax',
  'checkout.totalLabel': 'Total',
  'checkout.rowGiftCard': 'Gift card',
  'checkout.rowDeposit': 'Deposit paid',
  'checkout.rowCredit': 'Account credit',
  'checkout.rowToPay': 'To pay now',
  'checkout.howPaidQuestion': 'How was it paid?',
  /* The same six as `checkout.tender.*`, capitalised: buttons, not words in a sentence. */
  'checkout.tenderButton.cash': 'Cash',
  'checkout.tenderButton.check': 'Check',
  'checkout.tenderButton.venmo': 'Venmo',
  'checkout.tenderButton.paypal': 'PayPal',
  'checkout.tenderButton.card': 'Card',
  'checkout.tenderButton.other': 'Other',
  'checkout.useGiftCard': 'Use a gift card',
  'checkout.forgetGiftCard': 'Don’t use a gift card',
  'checkout.forgetCredit': 'Don’t use account credit',
  'checkout.splitPayment': 'Split payment',
  'checkout.addNote': 'Add a note to this sale',
  'checkout.charge': ({ amount, tender }: { amount: string; tender: string }) =>
    `Charge ${amount} · ${tender}`,
  'checkout.chargeAmount': ({ amount }: { amount: string }) => `Charge ${amount}`,
  'checkout.chargePlain': 'Charge',
  'checkout.chargeSplit': 'split',
  'checkout.completeSale': 'Complete sale — nothing left to pay',
  'checkout.whyEmpty': 'Add something to the sale first.',
  'checkout.whyTender': 'Choose how it was paid.',

  /* ---------------------------------------------------------------- */
  /* Reports — the ledger's six (module `reporting`)                   */
  /* ---------------------------------------------------------------- */

  'reports.notSwitchedOn': 'Reporting is not switched on for this business.',
  'reports.noneYet':
    'No reports on this device yet, and the ledger cannot be reached. They will appear the next time this iPad is online.',
  'reports.reading': 'Reading the ledger…',
  'reports.title': 'Reports',
  'reports.couldNotReach': ' · could not reach the ledger just now',
  'reports.last30': ({ from, to }: { from: string; to: string }) =>
    `Last 30 days · ${from} to ${to}`,
  'reports.moneyIn': 'Money in',
  'reports.givenAway': 'Given away',
  'reports.checkouts': 'Checkouts',
  'reports.visits': 'Visits',
  'reports.noShows': 'No-shows',
  'reports.upcoming': 'On the books ahead',
  'reports.sourceLine': ({
    settlement,
    deposit,
    certificate,
    topup,
  }: {
    settlement: string;
    deposit: string;
    certificate: string;
    topup: string;
  }) =>
    `${settlement} at the counter · ${deposit} deposits · ${certificate} certificates · ${topup} credit top-ups`,
  'reports.tenderLine': 'At the counter:',
  'reports.revenueByMonth': 'Revenue by month',
  'reports.colMonth': 'Month',
  'reports.colCounter': 'Counter',
  'reports.colDeposits': 'Deposits',
  'reports.colCertificates': 'Certificates',
  'reports.colCreditTopups': 'Credit top-ups',
  'reports.colTotal': 'Total',
  'reports.dateOfMoney':
    'Every cent on the date the money arrived — a deposit taken in March is March’s, whatever month the visit was.',
  'reports.whatSells': ({ from, to }: { from: string; to: string }) =>
    `What sells · ${from} to ${to}`,
  'reports.colItem': 'Item',
  'reports.colSold': 'Sold',
  'reports.colAtList': 'At list',
  'reports.colGivenAway': 'Given away',
  'reports.colNet': 'Net',
  'reports.nothingSold': 'Nothing sold in this window.',
  'reports.busiestDays': 'Busiest days',
  'reports.bestGuests': 'Best guests · the last year',
  'reports.lastVisit': ({ date }: { date: string }) => ` · last ${date}`,
  'reports.nobodyYet': 'Nobody yet.',
  'reports.worthACall': 'Worth a call',
  'reports.daysSince': ({ days, usual }: { days: number; usual: number }) =>
    `${String(days)} days since, usually every ${String(usual)}`,
  'reports.phoneTail': ({ phone }: { phone: string }) => ` · ${phone}`,
  'reports.nobodyOverdue': 'Nobody is overdue — everyone with a habit is keeping it.',

  /* ---------------------------------------------------------------- */
  /* Analytics — `reporting-api`, the read replica (dev.md §5)         */
  /* ---------------------------------------------------------------- */

  'analytics.heading': 'Deeper reports',
  'analytics.labelRevenue': 'Revenue',
  'analytics.labelBookings': 'Bookings',
  'analytics.labelUtilization': 'Utilization',
  'analytics.labelServices': 'Services',
  'analytics.labelGuests': 'Guests',
  'analytics.asking': 'Asking what is available…',
  'analytics.nonePublished': 'None published.',
  'analytics.navLabel': 'Reports',
  'analytics.last': 'Last',
  'analytics.days': 'days',
  'analytics.by': 'By',
  'analytics.grainDay': 'day',
  'analytics.grainWeek': 'week',
  'analytics.grainMonth': 'month',
  'analytics.cap': ({ cap }: { cap: number }) =>
    `This report answers at most ${String(cap)} days at once`,
  'analytics.capAsking': ({ asked }: { asked: number }) => ` — asking for ${String(asked)}`,
  'analytics.noTable': ({ id }: { id: string }) =>
    `This app has no table for “${id}” yet. The service publishes it at`,
  'analytics.reading': 'Reading…',
  'analytics.couldNotRead': ({ message }: { message: string }) => `Could not read it: ${message}`,
  'analytics.window': ({ from, to }: { from: string; to: string }) => `${from} to ${to}`,
  'analytics.clamped': ' · narrowed to this report’s cap',
  'analytics.noMoney': 'No money in this window.',
  'analytics.credit': 'Credit',
  'analytics.cashTopUps': 'Cash top-ups — income',
  'analytics.cashSpent': 'Cash spent — income on an earlier date',
  'analytics.giftIssued': 'Gift issued — never income',
  'analytics.giftSpent': 'Gift spent — a discount',
  'analytics.recognized': ({ total, count }: { total: string; count: number }) =>
    `${total} recognized over ${String(count)} ${plural(count, 'checkout', 'checkouts')}.`,
  'analytics.colSource': 'Source',
  'analytics.colBookings': 'Bookings',
  'analytics.colCovers': 'Covers',
  'analytics.colCancelled': 'Cancelled',
  'analytics.colNoShows': 'No-shows',
  'analytics.colMedianLead': 'Median lead',
  'analytics.colSample': 'Sample',
  'analytics.rowAll': 'All',
  'analytics.leadHours': ({ hours }: { hours: number }) => `${String(hours)} h`,
  'analytics.colResource': 'Resource',
  'analytics.colLanes': 'Lanes',
  'analytics.colOpenHours': 'Open h',
  'analytics.colSellableHours': 'Sellable h',
  'analytics.colOccupiedHours': 'Occupied h',
  'analytics.colUsed': 'Used',
  'analytics.nothingOpen': 'Nothing open in this window.',
  'analytics.colBooked': 'Booked',
  'analytics.colAvgParty': 'Avg party',
  'analytics.namesIn': ({ locale }: { locale: string }) => `Names in ${locale}.`,
  'analytics.tileGuests': 'Guests',
  'analytics.tileNew': 'New',
  'analytics.tileReturning': 'Returning',
  'analytics.tileRepeatRate': 'Repeat rate',
  'analytics.colGuest': 'Guest',
  'analytics.colVisits': 'Visits',
  'analytics.colLifetime': 'Lifetime',
  'analytics.colFirstSeen': 'First seen',
  'analytics.colLastSeen': 'Last seen',

  /* ---------------------------------------------------------------- */
  /* Orders — the kitchen pass (§3.6)                                  */
  /* ---------------------------------------------------------------- */

  'parcels.notSwitchedOn': 'The shop is not switched on for this business.',
  'parcels.title': 'Parcels',
  'parcels.couldNotReach': ' · could not reach the shop just now',
  'parcels.reading': 'Reading the parcels…',
  'parcels.none': 'No parcels yet.',
  'parcels.lanePaid': 'Waiting to go',
  'parcels.laneSubmitted': 'With the maker',
  'parcels.laneShipped': 'On its way',
  'parcels.stuckHeading': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'parcel', 'parcels')} did not go out`,
  'parcels.stuckTitle': ({ name }: { name: string }) => `${name}’s parcel did not go out`,
  'parcels.stuckBody': ({
    attempts,
    total,
    clock,
  }: {
    attempts: number;
    total: string;
    clock: string;
  }) =>
    `Paid ${total}, placed ${clock}. The maker refused it ${String(attempts)} ${plural(attempts, 'time', 'times')} and the retries are used up. Nothing has been sent.`,
  'parcels.lastError': ({ error }: { error: string }) => `Last answer: ${error}`,
  'parcels.retry': 'Send it again',
  'parcels.markDelivered': 'Delivered',
  'parcels.deliveredHeading': 'Delivered',
  'parcels.itemsAndTotal': ({ count, total }: { count: number; total: string }) =>
    `${String(count)} ${plural(count, 'item', 'items')} · ${total}`,
  'parcels.placedAt': ({ clock }: { clock: string }) => `Placed ${clock}`,
  'parcels.providerRef': ({
    provider,
    ref,
    draft,
  }: {
    provider: string;
    ref: string;
    draft: boolean;
  }) => `${provider} #${ref}${draft ? ' (draft)' : ''}`,

  'orders.notSwitchedOn': 'Ordering is not switched on for this business.',
  'orders.title': 'Orders',
  'orders.couldNotReach': ' · could not reach the kitchen just now',
  'orders.reading': 'Reading the pass…',
  'orders.noneOn': ({ day }: { day: string }) => `No orders on ${day}.`,
  'orders.lanePlaced': 'Placed',
  'orders.laneConfirmed': 'Confirmed',
  'orders.lanePreparing': 'Preparing',
  'orders.laneReady': 'Ready',
  'orders.laneCompleted': 'Collected',
  /**
   * The verbs on the buttons. **Which** of them a ticket gets comes from
   * `next_statuses` on the row — there is no transition table in this app and
   * there must not be — so this is a vocabulary, not a claim about what is legal.
   */
  'orders.verbPlaced': 'Reopen',
  'orders.verbConfirmed': 'Confirm',
  'orders.verbPreparing': 'Start',
  'orders.verbReady': 'Ready',
  'orders.verbCompleted': 'Collected',
  'orders.verbCancelled': 'Cancel',
  'orders.counter': 'Counter',
  'orders.itemsAndTotal': ({ count, total }: { count: number; total: string }) =>
    `${String(count)} ${plural(count, 'item', 'items')} · ${total}`,
  'orders.pickup': ({ clock }: { clock: string }) => `pickup ${clock}`,
  'orders.toCollect': ({ amount }: { amount: string }) => `${amount} to collect`,
  'orders.paid': 'paid',
  'orders.cancelledHeading': 'Cancelled',
  'orders.cancelledSummary': ({ total, clock }: { total: string; clock: string }) =>
    `${total} · ${clock}`,
  'orders.dialogLabel': 'Order',
  'orders.counterOrder': 'Counter order',
  'orders.placedAt': ({ clock }: { clock: string }) => `Placed ${clock}`,
  'orders.pickupTail': ({ clock }: { clock: string }) => ` · pickup ${clock}`,
  'orders.qty': ({ qty }: { qty: number }) => `${String(qty)}×`,
  'orders.sized': ({ name, size }: { name: string; size: string }) => `${name} · ${size}`,
  'orders.modifierWithPrice': ({ name, amount }: { name: string; amount: string }) =>
    `${name} ${amount}`,
  'orders.alreadyPaid': ({ amount }: { amount: string }) => `${amount} already paid · `,
  'orders.paidInFull': 'paid in full',
  /* The kitchen's own controls (docs/verticals-plan.md V1). */
  'orders.prepMinutes': 'Prep minutes',
  'orders.prepPlaceholder': 'min',
  'orders.prepTail': ({ minutes }: { minutes: number }) => ` · ${String(minutes)} min`,
  'orders.boardTitle': '86 board',
  'orders.boardShow': 'Show the menu',
  'orders.boardHide': 'Hide the menu',
  'orders.boardReading': 'Reading the menu…',
  'orders.boardEmpty': 'No dishes on the menu yet.',
  'orders.boardNote':
    'Out tonight, not off the menu: a dish marked 86 stays on the guest’s menu as sold out, and an order naming it is refused.',
  'orders.boardRetired': 'retired',
  'orders.soldOut': 'sold out',
  'orders.eightySix': 'Mark 86',
  'orders.backOn': 'Back on',
  'orders.flipChoice': ({ name, verb }: { name: string; verb: string }) => `${verb}: ${name}`,

  /* ---------------------------------------------------------------- */
  /* Departures — the dock (§4.8)                                      */
  /* ---------------------------------------------------------------- */

  'departures.notSwitchedOn': 'Departures are not switched on for this business.',
  'departures.title': 'Departures',
  'departures.couldNotReach': ' · could not reach the office just now',
  'departures.reading': 'Reading the schedule…',
  'departures.noneOn': ({ day }: { day: string }) => `Nothing is scheduled to sail on ${day}.`,
  'departures.noVessel': 'no vessel named',
  'departures.privateCharter': ' · private charter',
  'departures.weather': 'Weather',
  'departures.cancelled': 'Cancelled',
  'departures.reasonTail': ({ reason }: { reason: string }) => ` — ${reason}`,
  'departures.seats': ({ claimed, total }: { claimed: number; total: number }) =>
    `${String(claimed)}/${String(total)} seats`,
  'departures.parties': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'party', 'parties')}`,
  'departures.manifest': 'Manifest',
  'departures.readingManifest': 'Reading the manifest…',
  'departures.notFound': 'Not found.',
  'departures.sheetTitle': ({ name, clock }: { name: string; clock: string }) =>
    `${name} · ${clock}`,
  'departures.summaryLine': ({
    vessel,
    claimed,
    total,
    parties,
  }: {
    vessel: string;
    claimed: number;
    total: number;
    parties: string;
  }) => `${vessel} · ${String(claimed)} of ${String(total)} seats · ${parties}`,
  'departures.cancelledTail': ({ word, reason }: { word: string; reason: string | null }) =>
    reason === null ? ` · ${word}` : ` · ${word} — ${reason}`,
  'departures.weatherLower': 'weather',
  'departures.cancelledLower': 'cancelled',
  'departures.seatCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'seat', 'seats')}`,
  'departures.lanes': ({ lanes }: { lanes: string }) => ` · lanes ${lanes}`,
  'departures.ticketLine': ({ seats, name }: { seats: number; name: string }) =>
    `${String(seats)}× ${name}`,
  'departures.ticketsTail': ({ tickets }: { tickets: string }) => ` · ${tickets}`,
  'departures.nobodyBooked': 'Nobody booked.',
  'departures.entryCancelled': ' · cancelled',
  /*
   * The waiver, at the gangway (owner's ruling 6). **Acknowledged, never
   * signed**: what the guest did was tick a box after the text was on screen,
   * and the manifest says exactly that.
   *
   * "Not asked" rather than "missing" or "refused", because that is the only
   * thing a `null` can honestly mean here — the booking would not exist if a
   * published waiver had gone unacknowledged.
   */
  'departures.waiverOk': ({ version }: { version: number }) =>
    ` · acknowledged (v${String(version)})`,
  'departures.waiverNone': ' · waiver not asked',
  /*
   * After the call, one word per party (V7 / D4). "Texted" means queued
   * through the outbox — whether it left is the carrier's to say. "No
   * consent" is spec §2.6's one rule showing on the sheet: a number on file
   * and no tick is a phone call the desk still owes, and the sheet says so
   * rather than letting the captain assume everyone was told.
   */
  'departures.noticeTexted': ' · texted',
  'departures.noticeNoConsent': ' · not texted — no consent',
  'departures.noticeNoPhone': ' · not texted — no phone',
  'departures.called': ({ bookings, seats }: { bookings: number; seats: number }) =>
    `Called. ${String(bookings)} ${plural(bookings, 'booking', 'bookings')} cancelled and ${String(seats)} ${plural(seats, 'seat', 'seats')} released. No fee was charged; any money already taken is refunded from the PayPal dashboard.`,
  'departures.calledTexted': ({ texted, notTexted }: { texted: number; notTexted: number }) =>
    ` ${String(texted)} ${plural(texted, 'guest', 'guests')} texted; ${String(notTexted)} could not be — see each row.`,
  /**
   * The consequence, quoted from the row on screen. Three pieces because two
   * `<strong>` numbers sit inside it, and the shape — *verb, a count, a noun, a
   * count, the rest* — survives translation intact.
   */
  'departures.confirmLead': 'This cancels ',
  'departures.confirmMid': ({ count }: { count: number }) =>
    plural(count, ' booking covering ', ' bookings covering '),
  'departures.confirmTail': ({ count }: { count: number }) =>
    `${plural(count, ' seat', ' seats')}, and notifies the partner for each one; every guest who agreed to texts is texted. No cancellation fee is charged; a refund of anything already taken is the owner’s action in PayPal.`,
  'departures.why': 'Why',
  'departures.reasonPlaceholder': 'Six-foot swell on the north drop',
  'departures.reasonKind': 'Reason kind',
  'departures.kindWeather': 'Weather — the sea said no',
  'departures.kindOperator': 'Operator — we decided not to run it',
  'departures.keepIt': 'Keep it',
  'departures.calling': 'Calling…',
  'departures.cancelN': ({ count }: { count: number }) =>
    `Cancel ${String(count)} ${plural(count, 'booking', 'bookings')}`,
  'departures.weatherCall': 'Weather call',
  /* V5 — selling a seat from the manifest sheet. */
  'departures.sellSeat': 'Sell a seat',
  'departures.sellTitle': 'Sell a seat',
  'departures.tierSeats': ({ name, price }: { name: string; price: string }) =>
    `${name} · ${price} each`,
  'departures.seatsLeft': ({ count }: { count: number }) =>
    `${String(count)} ${count === 1 ? 'seat' : 'seats'} left`,
  'departures.total': 'Total fare',
  'departures.totalNote':
    'The fare is the catalog’s; the server prices the seats again when it sells them.',
  'departures.waiverTitle': ({ title, version }: { title: string; version: number }) =>
    `${title} (v${String(version)})`,
  'departures.waiverTick': 'Read to the guest and acknowledged at the desk',
  'departures.errorSeats': 'Choose at least one seat.',
  'departures.errorWaiver': 'The guest has to acknowledge the waiver before a seat is sold.',
  'departures.selling': 'Selling…',
  'departures.sell': ({ count }: { count: number }) =>
    `Sell ${String(count)} ${count === 1 ? 'seat' : 'seats'}`,
  'departures.sold': ({ name, count }: { name: string; count: number }) =>
    `${String(count)} ${count === 1 ? 'seat' : 'seats'} sold to ${name}.`,
  'departures.queued': 'Queued — the seat is sold the moment the office can be reached.',

  /* ---------------------------------------------------------------- */
  /* Front desk — lodging (§4.7)                                       */
  /* ---------------------------------------------------------------- */

  'frontdesk.notSwitchedOn': 'Lodging is not switched on for this business.',
  'frontdesk.couldNotReach': ' · could not reach the desk just now',
  'frontdesk.reading': 'Reading the day…',
  'frontdesk.arriving': 'Arriving',
  'frontdesk.arrivingBlank': 'Nobody is checking in.',
  'frontdesk.leaving': 'Leaving',
  'frontdesk.leavingBlank': 'Nobody is checking out.',
  'frontdesk.inHouse': 'In house',
  'frontdesk.inHouseBlank': 'The house is empty.',
  'frontdesk.room': ({ room }: { room: number }) => `room ${String(room)}`,
  'frontdesk.nightsAndGuests': ({ nights, guests }: { nights: number; guests: number }) =>
    `${String(nights)} ${plural(nights, 'night', 'nights')} · ${String(guests)} ${plural(guests, 'guest', 'guests')}`,
  'frontdesk.nothingTaken': 'nothing taken yet',
  'frontdesk.paidInFull': 'paid in full',
  'frontdesk.depositHeld': ({ amount }: { amount: string }) => `${amount} deposit held`,
  'frontdesk.dialogLabel': 'Stay',
  /** `status` is the server's word and is spliced in exactly as it came. */
  'frontdesk.staySummary': ({
    roomType,
    room,
    nights,
    guests,
    status,
  }: {
    roomType: string;
    room: number;
    nights: number;
    guests: number;
    status: string;
  }) =>
    `${roomType} · room ${String(room)} · ${String(nights)} ${plural(nights, 'night', 'nights')} · ${String(guests)} ${plural(guests, 'guest', 'guests')} · ${status}`,
  'frontdesk.folio': 'Folio',
  'frontdesk.takenAlready': 'Taken already',
  'frontdesk.stillToCollect': 'Still to collect',
  'frontdesk.feeRecorded': 'Cancellation fee recorded',
  'frontdesk.waived': ({ amount }: { amount: string }) =>
    `waived — the policy would have charged ${amount}`,
  'frontdesk.policyFee': ({ tier }: { tier: string | null }) =>
    tier === null ? "the policy's fee" : `the policy's fee · tier ${tier}`,
  'frontdesk.daysOutLine': ({ days, amount }: { days: number; amount: string }) =>
    `· ${String(days)} ${plural(days, 'day', 'days')} out · against ${amount} of accommodation`,
  'frontdesk.policyRef': ({ ref }: { ref: string }) => ` · ${ref}`,
  'frontdesk.recordedNotCharged':
    'Recorded, not charged. Refunding anything already taken is the owner’s action in PayPal (§4.7).',
  /**
   * Four pieces, because two `<strong>` runs carry the emphasis §4.7 asks for:
   * *whose* fee it is, and that it is recorded rather than charged.
   */
  'frontdesk.confirmLead': 'The fee is the ',
  'frontdesk.confirmPolicy': 'policy’s',
  'frontdesk.confirmMid': ({ nights, amount }: { nights: number; amount: string }) =>
    `, computed by the server against the ${String(nights)} ${plural(nights, 'night', 'nights')} as they were sold (${amount}) and how far out today is. It is `,
  'frontdesk.confirmStrong': 'recorded, never charged',
  'frontdesk.confirmTail':
    ' — no money moves either way, and the exact amount is shown here the moment it is computed. The desk’s decision is the one the policy cannot make.',
  'frontdesk.keepBooking': 'Keep the booking',
  'frontdesk.cancelWaive': 'Cancel and waive the fee',
  'frontdesk.cancelCharge': 'Cancel and charge the policy fee',
  'frontdesk.cancelling': 'Cancelling…',
  'frontdesk.cancelStay': 'Cancel this stay',
  /* V5 — booking a stay from the desk. */
  'frontdesk.bookStay': 'Book a stay',
  'frontdesk.bookDialogLabel': 'Book a stay',
  'frontdesk.checkIn': 'Check-in',
  'frontdesk.checkOut': 'Check-out',
  'frontdesk.guestsField': 'Guests',
  'frontdesk.roomField': 'Room',
  'frontdesk.pickARoom': 'Pick a room…',
  'frontdesk.roomOption': ({
    name,
    price,
    nights,
  }: {
    name: string;
    price: string;
    nights: number;
  }) => `${name} · ${price} for ${String(nights)} ${nights === 1 ? 'night' : 'nights'}`,
  'frontdesk.roomFull': ({ name }: { name: string }) => `${name} · none free for those dates`,
  'frontdesk.quoting': 'Pricing those dates…',
  'frontdesk.quoteFailed': 'Could not price those dates.',
  'frontdesk.quoteBreakdown': ({
    nightly,
    fees,
    tax,
  }: {
    nightly: string;
    fees: string;
    tax: string;
  }) => `${nightly} nights · ${fees} fees · ${tax} tax`,
  'frontdesk.quoteNote':
    'The total is the server’s; it prices the calendar again when it writes the folio.',
  'frontdesk.errorDates': 'Check-out must be after check-in.',
  'frontdesk.errorRoom': 'Pick a room.',
  'frontdesk.booking': 'Booking…',
  'frontdesk.book': 'Book',
  'frontdesk.booked': ({ name, room }: { name: string; room: string }) =>
    `${name} is booked into ${room}.`,
  'frontdesk.queued': 'Queued — the stay is booked the moment the desk can be reached.',
  /* V3 — the calendars two systems share. Never silently: §4.7's whole posture
     is that a channel we cannot hear from is a fact the desk is told, not a
     quiet gap in what is offered. The reason line is the sweep's own sentence
     and never carries a URL, which is a credential. */
  'frontdesk.calendarsHeading': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'calendar', 'calendars')} we have not heard from`,
  'frontdesk.calendarRoom': ({ room_type, room }: { room_type: string; room: number }) =>
    `${room_type} · room ${String(room)}`,
  'frontdesk.calendarNeverPulled': 'never pulled',
  'frontdesk.calendarLastError': ({ error }: { error: string }) => `last try: ${error}`,
  'frontdesk.calendarStaleNote': ({ hours }: { hours: number }) =>
    `A calendar is listed here once we have not managed to read it for ${String(hours)} ${plural(hours, 'hour', 'hours')}, or when the last try failed. Bookings made on that channel since then are not in this house’s calendar — check the channel itself before giving the room away.`,
  'frontdesk.collisionsHeading': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'room', 'rooms')} sold twice`,
  'frontdesk.collisionTitle': ({ room_type, room }: { room_type: string; room: number }) =>
    `${room_type}, room ${String(room)} — booked on both sides`,
  'frontdesk.collisionOurs': ({ name, from, to }: { name: string; from: string; to: string }) =>
    `Ours: ${name}, ${from} to ${to}.`,
  'frontdesk.collisionTheirs': ({
    calendar,
    summary,
    from,
    to,
  }: {
    calendar: string;
    summary: string;
    from: string;
    to: string;
  }) => `Theirs: ${calendar} — ${summary}, ${from} to ${to}.`,
  'frontdesk.collisionNothingCancelled':
    'Nothing has been cancelled and nothing will be automatically. Decide which booking moves, then cancel that one from its own screen; this card goes when one side does.',

  /* ---------------------------------------------------------------- */
  /* Payment links (§3.7)                                              */
  /* ---------------------------------------------------------------- */

  'paylink.dialogLabel': 'Payment link',
  'paylink.title': 'Payment link',
  'paylink.approvedNotCaptured': ' · approved, not captured',
  'paylink.expires': ({ clock }: { clock: string }) => ` · expires ${clock}`,
  'paylink.paidAt': ({ clock }: { clock: string }) => ` · paid ${clock}`,
  'paylink.copy': 'Copy',
  'paylink.copied': 'Copied',
  'paylink.none': 'No link has been sent for this booking.',
  'paylink.amountLabel': 'Amount — owner’s override (§6.5)',
  'paylink.amountPlaceholder': 'leave blank for the deposit policy',
  'paylink.amountHint':
    'Blank means the tenant’s deposit policy prices it, which is the only thing a partner or the guest site can ever get. A number here is the owner charging what they choose at their own till, and the link records that it was staff-priced.',
  'paylink.notAnAmount': 'That is not an amount. Leave it blank to use the deposit policy.',
  'paylink.creating': 'Creating…',
  'paylink.create': 'Create link',
  /* ---------------------------------------------------------------- */
  /* Albums (docs/tour-photos-design.md decision 5)                    */
  /* ---------------------------------------------------------------- */

  'albums.notSwitchedOn': 'Photos are not switched on for this business.',
  'albums.title': 'Albums',
  'albums.couldNotReach': ' · could not reach the office just now',
  'albums.reading': 'Reading the albums…',
  'albums.none': 'No albums yet. Make one for today’s trip.',
  'albums.untitled': 'Untitled album',
  'albums.photoCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'photo', 'photos')}`,
  'albums.fromSailing': ' · from a sailing',
  'albums.takenDown': ' · taken down',
  'albums.expires': ({ date }: { date: string }) => `until ${date}`,
  'albums.titleLabel': 'Album name',
  'albums.titlePlaceholder': 'Sunset run, the Miller family…',
  'albums.create': 'Make an album',
  'albums.creating': 'Making…',
  'albums.dialog': 'Album',
  'albums.addPhotos': 'Add photos',
  'albums.addPhotosHint': ({ max }: { max: number }) =>
    `Up to ${String(max)} at a time. They upload straight from this device.`,
  'albums.uploadsNeedConnection':
    'Uploads need a connection. Everything else in the app still works offline — photos are the one thing that cannot wait in the queue.',
  'albums.uploadWaiting': 'waiting',
  'albums.uploadPercent': ({ percent }: { percent: number }) => `${String(percent)}%`,
  'albums.uploadDone': 'done',
  'albums.uploadFailed': 'failed — send it again',
  'albums.notifyPhone': 'Text the family',
  'albums.notifyPhonePlaceholder': 'their mobile — or leave blank for the guest on the album',
  'albums.notify': 'Send “your photos are ready”',
  'albums.notified': ({ phone }: { phone: string }) =>
    `Sent to ${phone}: the link to the photos page. The code stays on the card.`,
  'albums.regenerate': 'New code',
  'albums.regenerateHint':
    'A new code stops the old card working straight away. Use it if a code went to the wrong family.',

  /* The code, shown once. */
  'albums.codeDialog': 'The album code',
  'albums.codeTitle': 'Write this on the card',
  /**
   * The sentence that makes `code_hash` honest, printed **beside the code** at
   * the moment it can still be acted on rather than discovered in a support
   * call. The CLI's `keys issue` says the same thing in the same place and for
   * the same reason.
   */
  'albums.codeOnce':
    'This is the only time the code is shown. We keep a fingerprint of it, not the code, so it cannot be looked up again — if it is lost, make a new one.',
  'albums.copy': 'Copy',
  'albums.copied': 'Copied',
  'albums.gotIt': 'Got it',
  'albums.forSailing': 'Album',

  /* ---------------------------------------------------------------- */
  /* The concierge — the owner's panel                                 */
  /*                                                                   */
  /* aiconcierge's own tokens (`balanced`, `ai_first`) are printed as   */
  /* they arrive, the way module slugs are: an owner reading one word   */
  /* here and another on a support call would hold two names for one    */
  /* setting. What is translated is the sentence beside each token —    */
  /* the plain-language meaning from the provisioning doc §5.           */
  /* ---------------------------------------------------------------- */

  'concierge.title': 'The AI front desk',
  'concierge.note':
    'It answers the phone and the chat. What it knows — your hours, prices, policies and questions — comes from this app; what it does is set here.',
  'concierge.notConfigured':
    'Concierge integration is not configured on this deployment. Nothing is missing from your business — there is simply no agent connected here.',
  'concierge.notEnabled':
    'The AI front desk is not switched on for this business yet. Ask whoever set the account up.',
  'concierge.unreachable': ({ message }: { message: string }) =>
    `Could not reach the agent just now: ${message}`,
  'concierge.status': 'Status',
  'concierge.knows': 'Knows your',
  'concierge.menuVersion': ({ version, when }: { version: string; when: string }) =>
    `menu (v${version}, read ${when})`,
  'concierge.detailsVersion': ({ version, when }: { version: string; when: string }) =>
    `details (v${version}, read ${when})`,
  'concierge.neverSynced': 'nothing read yet',
  'concierge.missing': 'Still to do',
  'concierge.missing.provider_connection':
    'The agent cannot read your book. Ask whoever set the account up to re-connect it.',
  'concierge.missing.catalog': 'Nothing bookable is published yet — add a service.',
  'concierge.missing.phone_number':
    'No telephone number yet: chat works, the phone and texts do not.',
  'concierge.missing.owner_number': 'Nobody to text when it needs you. Set your number below.',
  'concierge.openConsole': 'Open the agent’s console',

  'concierge.autonomy': 'How much it may decide',
  'concierge.autonomy.conservative':
    'Answers and takes the details but books nothing: it proposes, you confirm.',
  'concierge.autonomy.balanced':
    'Books anything on your menu it is allowed to, and asks you about anything custom, discounted or unhappy — with a reply already drafted.',
  'concierge.autonomy.autonomous':
    'Books the same without asking first, and only comes to you for something custom or above the ceiling.',
  'concierge.ceiling': 'Always ask above (cents)',
  'concierge.greeting': 'First words in chat',
  'concierge.voiceGreeting': 'First words on the phone',
  'concierge.voiceGreetingHint': 'Keep it under about twelve words — it is said out loud.',
  'concierge.toneVoice': 'How it should sound, in one sentence',
  'concierge.formality': 'Formality',
  'concierge.avoid': 'Words it must never use',
  'concierge.avoidHint': 'One per line.',
  'concierge.ownerNumber': 'Your number',
  'concierge.ownerNumberHint': 'Like +13405550100. Where it texts you when it needs you.',
  'concierge.voiceMode': 'On the phone',
  'concierge.voiceMode.disabled': 'No phone — chat and texts only.',
  'concierge.voiceMode.ai_first': 'It answers.',
  'concierge.voiceMode.owner_first': 'It rings you first and picks up what you miss.',

  'concierge.services': 'What it may book',
  'concierge.servicesNote': 'One switch per service on your menu.',
  'concierge.servicesEmpty': 'It has not read your menu yet — use Sync now.',
  'concierge.notSynced': 'not synced yet — Sync now',
  'concierge.mayBook': 'the AI may book this',

  'concierge.sync': 'Sync now',
  'concierge.syncNote': 'Usually unnecessary — an edit reaches it within seconds.',
  'concierge.pause': 'Pause',
  'concierge.resume': 'Switch it back on',
  'concierge.paused':
    'Paused: it still answers and takes a message for you — it just books nothing.',
  'concierge.save': 'Save',
  'concierge.saved': 'Saved.',
  'concierge.nothingChanged': 'Nothing changed.',

  /* ---------------------------------------------------------------- */
  /* The shelf — pickup reservations (docs/lovespa-gaps-plan.md C1)    */
  /* ---------------------------------------------------------------- */

  'reservations.notSwitchedOn': 'Products are not switched on for this business.',
  'reservations.title': 'Reservations',
  'reservations.couldNotReach': ' · could not reach the shelf just now',
  'reservations.reading': 'Reading the shelf…',
  'reservations.none': 'Nothing is waiting to be collected.',
  'reservations.someone': 'Someone',
  'reservations.atPickup': 'at pickup',
  'reservations.qty': ({ qty }: { qty: number }) => `${String(qty)}×`,
  'reservations.heldUntil': ({ date }: { date: string }) => `Held until ${date}`,
  'reservations.lanePlaced': 'To put aside',
  'reservations.laneReady': 'Ready to collect',
  'reservations.laneCollected': 'Collected',
  'reservations.laneClosed': 'Closed',
  'reservations.verbPlaced': 'Put aside',
  'reservations.verbReady': 'Mark ready',
  'reservations.verbCollected': 'Collected',
  'reservations.verbCancelled': 'Cancel',
  'reservations.verbExpired': 'No longer held',
  'reservations.ringUp': 'Ring it up',
  'reservations.ringUpHint': 'Open Checkout — these lines are waiting there.',
  'reservations.tillNote':
    'Marking a reservation collected records that it left the shelf. The money is rung up at the till like any other sale.',

  /* ---------------------------------------------------------------- */
  /* The newsletter (E3 - Newsletter.tsx)                              */
  /* ---------------------------------------------------------------- */

  'newsletter.notSwitchedOn': 'The client book is not switched on for this business.',
  'newsletter.reading': 'Reading your newsletters...',
  'newsletter.couldNotReach': 'Could not reach the server. This screen needs a connection.',
  'newsletter.noneYet': 'Nothing written yet. Tap "Write one" to start.',
  'newsletter.write': 'Write one',
  'newsletter.newSubject': 'This is my new stuff',
  'newsletter.untitled': 'Untitled',
  'newsletter.subject': 'Subject',
  'newsletter.preheader': 'Preview line',
  'newsletter.preheaderHint': 'The grey line after the subject in an inbox',
  'newsletter.saved': 'Saved',
  'newsletter.preview': 'Preview',
  'newsletter.desktop': 'On a computer',
  'newsletter.phone': 'On a phone',
  'newsletter.sendNow': 'Send now',
  'newsletter.confirmSend': 'Send this to everyone on your list? This cannot be undone.',
  'newsletter.testMe': ({ email }: { email: string }) => `Send a test to ${email}`,
  'newsletter.testQueued': 'A test is on its way to you.',
  'newsletter.queued': ({ count }: { count: number }) =>
    `On its way to ${count} ${count === 1 ? 'person' : 'people'}.`,
  'newsletter.audience': ({ count }: { count: number }) =>
    count === 0
      ? 'Nobody is on your list yet.'
      : `${count} ${count === 1 ? 'person is' : 'people are'} on your list.`,
  'newsletter.from': ({ from }: { from: string }) => `It comes from ${from}`,
  'newsletter.replyTo': ({ to }: { to: string }) => `Replies go to ${to}`,
  'newsletter.joinLink': 'Put this link on a card so people can join:',
  'newsletter.counts': ({
    sent,
    failed,
    unsubscribed,
  }: {
    sent: number;
    failed: number;
    unsubscribed: number;
  }) => `${sent} sent - ${failed} did not arrive - ${unsubscribed} unsubscribed`,
  'newsletter.noTracking': 'We do not track who opened it. Nobody is watching your customers.',
  'newsletter.text': 'Words',
  'newsletter.textHint': 'Plain sentences. *bold*, _italic_, [a link](https://...)',
  'newsletter.imageUrl': 'Picture address',
  'newsletter.alt': 'What the picture shows',
  'newsletter.altHint': 'Many people see this instead of the picture',
  'newsletter.product': 'Which product',
  'newsletter.blurb': 'Your own line about it',
  'newsletter.priceFromCatalog':
    'The price and photo come from your shop, so they are never wrong.',
  'newsletter.label': 'Button text',
  'newsletter.linkUrl': 'Where it goes',
  'newsletter.block.heading': 'Heading',
  'newsletter.block.text': 'Words',
  'newsletter.block.image': 'Picture',
  'newsletter.block.product': 'Product',
  'newsletter.block.button': 'Button',
  'newsletter.block.divider': 'Line',
  'newsletter.status.draft': 'Draft',
  'newsletter.status.scheduled': 'Scheduled',
  'newsletter.status.sending': 'Sending',
  'newsletter.status.sent': 'Sent',
  'newsletter.status.cancelled': 'Cancelled',
};

/**
 * The shape every catalog must have — the reason a missing Spanish key is a
 * build failure rather than an English sentence on a Spanish till.
 *
 * Deliberately **not** `as const`: with literal types every Spanish string would
 * have to equal its English original, which is the opposite of the point. What
 * is pinned is the *set of keys* and the *parameter object* each function takes,
 * which is exactly the part a translation must not change.
 */
export type Messages = typeof en;
